import React, {
  useState,
  useContext,
  createContext,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Link,
  Code,
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Calculator,
  Sigma,
  Check,
  X,
  Trash2,
  Image,
  Plus,
} from "lucide-react";
// Import MathLive from local package
import "mathlive";
import { template } from "lodash";
import { getLocalStorageData } from "../../../services/axiosComponent";
// import "mathlive/dist/mathlive-fonts.css";
import html2canvas from "html2canvas";
import {
  updateExaminationTemplate,
  updateQuestion,
} from "../../../services/Users-services.js";
export const PAPER_SIZES = {
  A4: {
    name: "A4 (210×297mm)",
    width: 794, // ~210mm at 96dpi
    height: 1123, // ~297mm at 96dpi
    printWidth: "210mm",
    printHeight: "297mm",
  },
  A3: {
    name: "A3 (297×420mm)",
    width: 1123,
    height: 1587,
    printWidth: "297mm",
    printHeight: "420mm",
  },
  Letter: {
    name: "Letter (8.5×11in)",
    width: 816, // 8.5in at 96dpi
    height: 1056, // 11in at 96dpi
    printWidth: "8.5in",
    printHeight: "11in",
  },
  Legal: {
    name: "Legal (8.5×14in)",
    width: 816,
    height: 1344,
    printWidth: "8.5in",
    printHeight: "14in",
  },
  A5: {
    name: "A5 (148×210mm)",
    width: 559,
    height: 794,
    printWidth: "148mm",
    printHeight: "210mm",
  },
};

const MathLiveEditor = ({
  value,
  onChange,
  placeholder = "Enter equation...",
  onPreviewChange,
}) => {
  const mathFieldRef = useRef(null);
  const previewRef = useRef(null);
  const [mathFieldValue, setMathFieldValue] = useState(value || "");
  const [isLoaded, setIsLoaded] = useState(false);
  const lastValueRef = useRef(value || "");

  // Helper to check if MathLive is loaded
  const isMathLiveLoaded = () => {
    return window.MathLive && window.customElements.get("math-field");
  };

  // Check if MathLive is available
  useEffect(() => {
    const checkMathLive = () => {
      const isCustomElementDefined =
        window.customElements && window.customElements.get("math-field");

      if (isCustomElementDefined) {
        setIsLoaded(true);
        return true;
      }
      return false;
    };

    if (checkMathLive()) {
      return;
    }

    const checkReadiness = () => {
      return new Promise((resolve) => {
        if (checkMathLive()) {
          resolve(true);
          return;
        }

        if (window.customElements && window.customElements.whenDefined) {
          window.customElements
            .whenDefined("math-field")
            .then(() => {
              setIsLoaded(true);
              resolve(true);
            })
            .catch(() => {
              pollForMathLive(resolve);
            });
        } else {
          pollForMathLive(resolve);
        }
      });
    };

    const pollForMathLive = (resolve) => {
      const interval = setInterval(() => {
        if (checkMathLive()) {
          clearInterval(interval);
          resolve(true);
        }
      }, 100);

      setTimeout(() => {
        clearInterval(interval);
        if (!isLoaded) {
          console.warn("MathLive failed to load within 10 seconds");
          setIsLoaded(true);
        }
        resolve(false);
      }, 10000);
    };

    checkReadiness();
  }, [isLoaded]);

  useEffect(() => {
    if (value !== mathFieldValue) {
      setMathFieldValue(value || "");
      lastValueRef.current = value || "";
    }
  }, [value]);

  const renderPreview = (latex) => {
    if (!previewRef.current || !isLoaded) return;

    try {
      previewRef.current.value = latex || "";
    } catch (e) {
      console.log("Preview update error:", e);
    }
  };

  useEffect(() => {
    if (!isLoaded) return;

    const mathField = mathFieldRef.current;
    if (mathField && mathField.setOptions) {
      mathField.setOptions({
        virtualKeyboardMode: "manual",
        mathModeSpace: "\\;",
        plonkSound: null,
        keypressSound: null,
        virtualKeyboards: "numeric symbols greek functions command",
        virtualKeyboardLayout: "auto",
        smartMode: true,
        smartFence: true,
        smartSuperscript: true,
        inlineShortcuts: {
          pi: "\\pi",
          theta: "\\theta",
          alpha: "\\alpha",
          beta: "\\beta",
          gamma: "\\gamma",
          delta: "\\delta",
          infinity: "\\infty",
          sum: "\\sum",
          int: "\\int",
          sqrt: "\\sqrt{#0}",
          frac: "\\frac{#0}{#1}",
          sup: "^{#0}",
          sub: "_{#0}",
        },
      });

      // ✅ IMPROVED: Better virtual keyboard management
      const handleFocusIn = (event) => {
        event.stopPropagation();
        console.log("Math field focused - showing virtual keyboard");
        if (window.mathVirtualKeyboard) {
          window.mathVirtualKeyboard.show();
        }
      };

      const handleFocusOut = (event) => {
        event.stopPropagation();
        console.log("Math field blur detected");

        // ✅ CRITICAL: Don't hide keyboard immediately on blur
        // Instead, check if focus is moving to virtual keyboard
        setTimeout(() => {
          const activeElement = document.activeElement;
          const isVirtualKeyboardFocused =
            activeElement &&
            (activeElement.closest(".ML__keyboard") ||
              activeElement.classList.contains("ML__keycap") ||
              activeElement.closest(".ML__virtual-keyboard-toggle"));

          const isAnotherMathFieldFocused =
            activeElement && activeElement.tagName === "MATH-FIELD";
          const isVirtualKeyboardVisible =
            document.querySelector(".ML__keyboard");

          console.log("Focus check:", {
            isVirtualKeyboardFocused,
            isAnotherMathFieldFocused,
            isVirtualKeyboardVisible: !!isVirtualKeyboardVisible,
            activeElement: activeElement?.tagName,
          });

          // Only hide if focus moved completely away from math context
          if (
            window.mathVirtualKeyboard &&
            !isVirtualKeyboardFocused &&
            !isAnotherMathFieldFocused &&
            isVirtualKeyboardVisible
          ) {
            console.log("Hiding virtual keyboard");
            window.mathVirtualKeyboard.hide();
          }
        }, 200); // Increased delay to prevent premature hiding
      };

      // ✅ CRITICAL: Prevent virtual keyboard interactions from bubbling
      const handleVirtualKeyboardInteraction = (event) => {
        // Check if the event target is part of the virtual keyboard
        if (
          event.target.closest(".ML__keyboard") ||
          event.target.classList.contains("ML__keycap") ||
          event.target.closest(".ML__virtual-keyboard-toggle")
        ) {
          console.log(
            "Virtual keyboard interaction detected, preventing bubbling"
          );
          event.stopPropagation();
          event.stopImmediatePropagation();

          // Ensure the math field remains focused
          if (
            mathFieldRef.current &&
            document.activeElement !== mathFieldRef.current
          ) {
            setTimeout(() => {
              mathFieldRef.current.focus();
            }, 10);
          }
        }
      };

      // ✅ IMPROVED: Input handling with better change detection
      const handleInputEvent = (event) => {
        event.stopPropagation();
        const newValue = mathField.value || "";

        if (newValue !== lastValueRef.current) {
          console.log("Math field value changed:", newValue);
          lastValueRef.current = newValue;
          setMathFieldValue(newValue);
          onChange(newValue);
          renderPreview(newValue);

          if (onPreviewChange) {
            onPreviewChange(newValue);
          }
        }
      };

      // Add input event listeners
      const inputEvents = ["input", "change", "blur"];
      inputEvents.forEach((eventType) => {
        mathField.addEventListener(eventType, handleInputEvent);
      });

      // Add focus management
      mathField.addEventListener("focusin", handleFocusIn);
      mathField.addEventListener("focusout", handleFocusOut);

      // ✅ CRITICAL: Add global listeners for virtual keyboard interactions
      document.addEventListener(
        "mousedown",
        handleVirtualKeyboardInteraction,
        true
      );
      document.addEventListener(
        "click",
        handleVirtualKeyboardInteraction,
        true
      );
      document.addEventListener(
        "touchstart",
        handleVirtualKeyboardInteraction,
        true
      );

      // ✅ IMPROVED: Handle virtual keyboard button clicks specifically
      const handleKeyboardClick = (event) => {
        if (event.target.closest(".ML__keyboard")) {
          console.log("Virtual keyboard button clicked");
          event.stopPropagation();
          event.stopImmediatePropagation();

          // Keep focus on math field after virtual keyboard interaction
          setTimeout(() => {
            if (mathFieldRef.current) {
              mathFieldRef.current.focus();
            }
          }, 50);
        }
      };

      // Add specific keyboard click handler
      document.addEventListener("click", handleKeyboardClick, true);

      // ✅ IMPROVED: Polling with better conflict prevention
      const pollForChanges = setInterval(() => {
        if (mathField.value !== lastValueRef.current) {
          console.log("Polling detected change:", mathField.value);
          const newValue = mathField.value || "";
          lastValueRef.current = newValue;
          setMathFieldValue(newValue);
          onChange(newValue);
          renderPreview(newValue);
          if (onPreviewChange) {
            onPreviewChange(newValue);
          }
        }
      }, 1000); // Increased interval to reduce conflicts

      // Set initial value
      if (mathFieldValue) {
        mathField.value = mathFieldValue;
        renderPreview(mathFieldValue);
        lastValueRef.current = mathFieldValue;
      }

      return () => {
        // Clean up all event listeners
        inputEvents.forEach((eventType) => {
          mathField.removeEventListener(eventType, handleInputEvent);
        });
        mathField.removeEventListener("focusin", handleFocusIn);
        mathField.removeEventListener("focusout", handleFocusOut);

        document.removeEventListener(
          "mousedown",
          handleVirtualKeyboardInteraction,
          true
        );
        document.removeEventListener(
          "click",
          handleVirtualKeyboardInteraction,
          true
        );
        document.removeEventListener(
          "touchstart",
          handleVirtualKeyboardInteraction,
          true
        );
        document.removeEventListener("click", handleKeyboardClick, true);

        clearInterval(pollForChanges);
      };
    }
  }, [isLoaded, onChange, onPreviewChange, mathFieldValue]);

  useEffect(() => {
    if (previewRef.current && previewRef.current.setOptions && isLoaded) {
      previewRef.current.setOptions({
        readOnly: true,
        virtualKeyboardMode: "off",
      });
      if (mathFieldValue) {
        previewRef.current.value = mathFieldValue;
      }
    }
  }, [isLoaded, mathFieldValue]);

  if (!isLoaded) {
    return (
      <div className="mathlive-loading">
        <div className="loading-spinner"></div>
        <span>Loading math editor...</span>
      </div>
    );
  }

  return (
    <div className="mathlive-container">
      <div className="mathlive-input-section">
        <label className="mathlive-label">LaTeX Input:</label>
        <div className="math-field-wrapper">
          <math-field
            ref={mathFieldRef}
            virtual-keyboard-mode="manual"
            className="enhanced-math-field"
          />
          {!mathFieldValue && (
            <div className="mathlive-placeholder">{placeholder}</div>
          )}
        </div>
      </div>

      <div className="math-preview-section">
        <label className="mathlive-label">Preview:</label>
        <div className="math-preview-wrapper">
          <math-field
            ref={previewRef}
            read-only="true"
            className="enhanced-math-preview"
          />
        </div>
      </div>
    </div>
  );
};
// In utility.js - Add this helper function
export const ensureEquationsAreRestored = (content, globalEquations = {}) => {
  if (!content || !content.html) return content;

  let processedContent = { ...content };

  // Check if content has plain equation placeholders that need restoration
  const hasPlainPlaceholders = /\[EQUATION:([^\]]+)\]/.test(content.html);
  
  if (hasPlainPlaceholders && !content.equations && globalEquations) {
    // Restore equations from global metadata
    const contentEquations = {};
    const placeholderPattern = /\[EQUATION:([^\]]+)\]/g;
    let match;

    while ((match = placeholderPattern.exec(content.html)) !== null) {
      const equationId = match[1];
      if (globalEquations[equationId]) {
        contentEquations[equationId] = globalEquations[equationId];
      }
    }

    if (Object.keys(contentEquations).length > 0) {
      processedContent.equations = contentEquations;
    }
  }

  return processedContent;
};

// Utility Functions
export const generateId = () => Math.random().toString(36).substr(2, 9);

export const createNewPage = () => ({
  id: generateId(),
  padding: 20,
  borderStyle: "solid",
  pageBreakBefore: false,
  sections: [],
});

// In utility.js - Update the createNewSection function
// In utility.js, update the createNewSection function
export const createNewSection = (exam) => {
  const existingSections = exam.pages?.[0]?.sections || [];
  const nextSerialNumber = existingSections.length + 1;
  const sectionTitle = `Section ${nextSerialNumber}`;

  return {
    name: sectionTitle,
    templateId: exam.id,
    organizationId:
      getLocalStorageData("profileDetails")["_ORGANIZATION_"]["_ID"],
    regionId: exam.regionId,
    siteId: exam.siteId,
    serialNumber: nextSerialNumber,
    description: "Enter section instructions here...",
    // ✅ NEW: Add default rich content for title
    titleRichContent: {
      type: "rich-text",
      html: `<p>${sectionTitle}</p>`,
      plainText: sectionTitle,
    },
  };
};

// ✅ FIXED: Updated createNewQuestion function in utility.js
export const createNewQuestion = (exam, selectedNode) => {
  // ✅ FIXED: Ensure proper section ID resolution
  let targetSectionId = selectedNode?.id;
  console.log("Selected node for new question:", selectedNode);
  
  // If no section selected or section doesn't exist, use first available section
  if (!targetSectionId || selectedNode?.type !== "section") {
    const firstSection = exam.pages?.[0]?.sections?.[0];
    if (firstSection) {
      targetSectionId = firstSection.id;
    }
  }

  // ✅ FIXED: Create stable IDs that won't change on re-render
  const questionId = generateId();
  const timestamp = Date.now();
  
  // ✅ FIXED: Create proper options for OBJECTIVE exam type with stable IDs
  var options = [];
  console.log("Exam data", exam);
  
  if (exam.examType == "Objective" || exam.examType == "OBJECTIVE") {
    options = [
      {
        id: 1, // ✅ FIXED: Stable ID
        content: "Option A",
        richContent: {
          type: "rich-text",
          html: "<p>Option A</p>",
          plainText: "Option A",
        },
      },
      {
        id: 2, // ✅ FIXED: Stable ID
        content: "Option B",
        richContent: {
          type: "rich-text",
          html: "<p>Option B</p>",
          plainText: "Option B",
        },
      },
      {
        id: 3, // ✅ FIXED: Stable ID
        content: "Option C",
        richContent: {
          type: "rich-text",
          html: "<p>Option C</p>",
          plainText: "Option C",
        },
      },
      {
        id: 4, // ✅ FIXED: Stable ID
        content: "Option D",
        richContent: {
          type: "rich-text",
          html: "<p>Option D</p>",
          plainText: "Option D",
        },
      },
    ];
  }

  // ✅ FIXED: Create new question with proper structure and metadata handling
  const newQuestion = {
    id: questionId, // ✅ FIXED: Use the stable ID
    serialNumber: 1,
    sectionId: targetSectionId,
    templateId: exam.id,
    richContent: {
      type: "rich-text",
      html: "<p>Enter your question here...</p>",
      plainText: "Enter your question here...",
    },
    question: "Enter your question here...",
    marks: 1,
    score: 1,
    options: options,
    correctAnswers: [],
    rightOptions: [
      { id: 1, value: "Option A" },
    ], // ✅ FIXED: Initialize as empty array for new questions
    shuffleOptions: false,
    organizationId:
      getLocalStorageData("profileDetails")["_ORGANIZATION_"]["_ID"],
    regionId: exam.regionId,
    siteId: exam.siteId,
    isMultiSelect: "N",
    optionsLayout: "vertical",
    // ✅ FIXED: Set proper question type based on exam type
    type: (exam.examType == "Objective" || exam.examType == "OBJECTIVE") ? "with-options" : "without-options",
  };

  return newQuestion;
};

// ✅ NEW: Metadata management functions
export const createMetadataEntry = (
  sectionId,
  instructions = null,
  questions = []
) => ({
  instructions,
  questions: questions.map((question) => ({
    questionId: question.id,
    configuration: question.richContent || null,
  })),
});

export const updateMetadataForSection = (metadata, sectionId, instructions) => {
  const updatedMetadata = { ...metadata };

  if (!updatedMetadata[sectionId]) {
    updatedMetadata[sectionId] = createMetadataEntry(
      sectionId,
      instructions,
      []
    );
  } else {
    updatedMetadata[sectionId] = {
      ...updatedMetadata[sectionId],
      instructions,
    };
  }

  return updatedMetadata;
};

// ✅ FIXED: Enhanced updateMetadataForQuestion function in utility.js
export const updateMetadataForQuestion = (
  metadata,
  sectionId,
  questionId,
  configuration,
  questionData = null // ✅ NEW: Accept full question data for complete metadata
) => {
  const updatedMetadata = { ...metadata };

  if (!updatedMetadata[sectionId]) {
    updatedMetadata[sectionId] = createMetadataEntry(sectionId, null, []);
  }

  const questionIndex = updatedMetadata[sectionId].questions.findIndex(
    (q) => q.questionId === questionId
  );

  // ✅ FIXED: Create comprehensive question metadata
  const questionMetadata = {
    questionId,
    configuration,
    // ✅ FIXED: Include all question properties for complete metadata
    ...(questionData && {
      type: questionData.type,
      marks: questionData.marks,
      options: questionData.options?.map((option) => ({
        id: option.id,
        content: option.content,
        richContent: option.richContent,
      })) || [],
      correctAnswers: questionData.correctAnswers || [],
      rightOptions: questionData.rightOptions || [],
      isMultiSelect: questionData.isMultiSelect,
      optionsLayout: questionData.optionsLayout,
      shuffleOptions: questionData.shuffleOptions,
    }),
  };

  if (questionIndex >= 0) {
    // ✅ FIXED: Update existing question metadata while preserving existing properties
    updatedMetadata[sectionId].questions[questionIndex] = {
      ...updatedMetadata[sectionId].questions[questionIndex],
      ...questionMetadata,
    };
  } else {
    // ✅ FIXED: Add new question metadata
    updatedMetadata[sectionId].questions.push(questionMetadata);
  }

  return updatedMetadata;
};



export const removeMetadataForSection = (metadata, sectionId) => {
  const updatedMetadata = { ...metadata };
  delete updatedMetadata[sectionId];
  return updatedMetadata;
};

export const removeMetadataForQuestion = (metadata, sectionId, questionId) => {
  const updatedMetadata = { ...metadata };

  if (updatedMetadata[sectionId]) {
    updatedMetadata[sectionId] = {
      ...updatedMetadata[sectionId],
      questions: updatedMetadata[sectionId].questions.filter(
        (q) => q.questionId !== questionId
      ),
    };
  }

  return updatedMetadata;
};

export const getMetadataForSection = (metadata, sectionId) => {
  return metadata[sectionId]?.instructions || null;
};

export const getMetadataForQuestion = (metadata, sectionId, questionId) => {
  const sectionMeta = metadata[sectionId];
  if (!sectionMeta) return null;

  const questionMeta = sectionMeta.questions.find(
    (q) => q.questionId === questionId
  );
  return questionMeta?.configuration || null;
};

export const setInitialExamData = (data) => {
  const apiData = data;

  // Helper function to parse duration string (HH:MM:SS) to readable format
  const parseDuration = (durationStr) => {
    if (!durationStr || durationStr === "00:00:00") {
      return "Not specified";
    }
    const [hours, minutes] = durationStr.split(":");
    if (hours === "00" && minutes === "00") {
      return "Not specified";
    }
    return `${parseInt(hours)} Hours ${parseInt(minutes)} Minutes`;
  };

  const parseApiContent = (questionText) => {
    try {
      const parsedContent = JSON.parse(questionText);

      // ✅ NEW: Check if content is already structured with equations
      if (parsedContent.equations) {
        return parsedContent;
      }

      // ✅ NEW: Check if content has old-style math elements and convert them
      if (
        parsedContent.html &&
        parsedContent.html.includes("data-equation-id")
      ) {
        return convertLegacyEquations(parsedContent);
      }

      return {
        type: "rich-text",
        html: parsedContent.html || `<p>${questionText}</p>`,
        plainText: parsedContent.plainText || questionText,
      };
    } catch {
      return {
        type: "rich-text",
        html: `<p>${questionText || "Enter your question here..."}</p>`,
        plainText: questionText || "Enter your question here...",
      };
    }
  };
  const convertLegacyEquations = (content) => {
    const equations = {};
    let processedHtml = content.html;

    // Find existing math elements and extract equations
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content.html;

    const mathElements = tempDiv.querySelectorAll("[data-equation-id]");
    mathElements.forEach((element) => {
      const equationId = element.getAttribute("data-equation-id");
      const latex = element.getAttribute("data-latex");

      if (equationId && latex) {
        equations[equationId] = {
          id: equationId,
          latex: latex,
        };

        // Replace with placeholder
        const placeholder = document.createElement("span");
        placeholder.setAttribute("data-equation-id", equationId);
        placeholder.textContent = `[EQUATION:${equationId}]`;
        element.parentNode.replaceChild(placeholder, element);
      }
    });

    return {
      type: "rich-text",
      html: tempDiv.innerHTML,
      plainText: content.plainText,
      equations: Object.keys(equations).length > 0 ? equations : undefined,
    };
  };
  // Parse metadata if it exists
  let metadataConfig = null;
  let globalEquations = {};
  try {
    if (apiData._METADATA) {
      metadataConfig = JSON.parse(apiData._METADATA);
      globalEquations = metadataConfig.equationsMetadata || {};
    }
  } catch (error) {
    console.warn("Failed to parse metadata:", error);
  }
  // In utility.js - Update the parseContentWithEquations function inside setInitialExamData
  const parseContentWithEquations = (contentString, fallbackText = "") => {
    if (!contentString) {
      return {
        type: "rich-text",
        html: `<p>${fallbackText}</p>`,
        plainText: fallbackText,
      };
    }

    try {
      const parsedContent = JSON.parse(contentString);

      // If content already has equations, use it as-is
      if (parsedContent.equations) {
        return parsedContent;
      }

      // ✅ FIXED: Check for equation placeholders and restore from global equations
      if (parsedContent.html && parsedContent.html.includes("[EQUATION:")) {
        return restoreEquationsFromGlobal(parsedContent, globalEquations);
      }

      // ✅ FIXED: Also check plain text content for equation placeholders
      if (
        typeof parsedContent === "string" &&
        parsedContent.includes("[EQUATION:")
      ) {
        const htmlContent = `<p>${parsedContent}</p>`;
        return restoreEquationsFromGlobal(
          {
            type: "rich-text",
            html: htmlContent,
            plainText: parsedContent,
          },
          globalEquations
        );
      }

      return {
        type: "rich-text",
        html: parsedContent.html || `<p>${contentString}</p>`,
        plainText: parsedContent.plainText || contentString,
      };
    } catch {
      // ✅ FIXED: Handle plain text with equation placeholders
      if (contentString.includes("[EQUATION:")) {
        return restoreEquationsFromGlobal(
          {
            type: "rich-text",
            html: `<p>${contentString}</p>`,
            plainText: contentString,
          },
          globalEquations
        );
      }

      return {
        type: "rich-text",
        html: `<p>${contentString}</p>`,
        plainText: contentString,
      };
    }
  };

  // In utility.js - Update the restoreEquationsFromGlobal function
  const restoreEquationsFromGlobal = (content, globalEquations) => {
    const contentEquations = {};
    let processedHtml = content.html;

    // Find equation placeholders and match with global equations
    const placeholderPattern = /\[EQUATION:([^\]]+)\]/g;
    let match;

    while ((match = placeholderPattern.exec(content.html)) !== null) {
      const equationId = match[1];
      if (globalEquations[equationId]) {
        contentEquations[equationId] = globalEquations[equationId];
      }
    }

    // ✅ FIXED: Also wrap plain placeholders with span tags for better handling
    Object.keys(contentEquations).forEach((equationId) => {
      const plainPlaceholder = `[EQUATION:${equationId}]`;
      const wrappedPlaceholder = `<span data-equation-id="${equationId}">[EQUATION:${equationId}]</span>`;
      processedHtml = processedHtml.replace(
        new RegExp(escapeRegExp(plainPlaceholder), "g"),
        wrappedPlaceholder
      );
    });

    return {
      ...content,
      html: processedHtml,
      equations:
        Object.keys(contentEquations).length > 0 ? contentEquations : undefined,
    };
  };

  // Helper function to escape regex special characters
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  // Helper function to find configuration for a section from metadata
  const getSectionConfig = (sectionId) => {
    if (!metadataConfig?.pages?.[0]?.sections) return null;
    return metadataConfig.pages[0].sections.find((s) => s.id === sectionId);
  };

  // Helper function to find configuration for a question from metadata
  const getQuestionConfig = (sectionId, questionId) => {
    const sectionConfig = getSectionConfig(sectionId);
    if (!sectionConfig?.questions) return null;
    return sectionConfig.questions.find((q) => q.id === questionId);
  };

  // In utility.js, update the mapSections function within setInitialExamData
  const mapSections = (apiSections) => {
    if (!apiSections || !Array.isArray(apiSections)) {
      return [];
    }

    return apiSections.map((section) => {
      const sectionConfig = getSectionConfig(section.id);

      // Handle section title rich content
      let titleRichContent = null;
      if (sectionConfig?.titleRichContent) {
        titleRichContent = sectionConfig.titleRichContent;
      } else if (
        sectionConfig?.title &&
        typeof sectionConfig.title === "object"
      ) {
        titleRichContent = sectionConfig.title;
      }

      return {
        id: section.id,
        title: sectionConfig?.title || section.name || "New Section",
        titleRichContent: titleRichContent || {
          type: "rich-text",
          html: `<p>${
            sectionConfig?.title || section.name || "New Section"
          }</p>`,
          plainText: sectionConfig?.title || section.name || "New Section",
        },
        titleAlignment: sectionConfig?.titleAlignment || "left",
        hasInstructions:
          sectionConfig?.hasInstructions ||
          (section.description && section.description.trim() !== ""),
        instructions:
          sectionConfig?.instructions ||
          parseContentWithEquations(
            section.description,
            "Enter section instructions here..."
          ),
        marksAllotted:
          section.questionsList?.reduce(
            (total, q) => total + (q.score || 0),
            0
          ) || 0,
        questions: mapQuestions(section.questionsList || [], section.id),
      };
    });
  };

  const mapQuestions = (apiQuestions, sectionId) => {
    if (!apiQuestions || !Array.isArray(apiQuestions)) {
      return [];
    }

    return apiQuestions.map((question) => {
      const questionConfig = getQuestionConfig(sectionId, question.id);
      const hasOptions = question.options && question.options.length > 0;

      // ✅ UPDATED: Use parseContentWithEquations for question content
      let richContent;
      if (questionConfig?.richContent) {
        richContent = questionConfig.richContent;
      } else if (questionConfig?.configuration) {
        richContent = questionConfig.configuration;
      } else {
        richContent = parseContentWithEquations(
          question.question,
          "Enter your question here..."
        );
      }

      // ✅ FIXED: Map options with stable IDs and proper structure
      let mappedOptions = [];
      if (hasOptions) {
        // Check if we have options metadata from config
        if (questionConfig?.options && questionConfig.options.length > 0) {
          mappedOptions = questionConfig.options.map((optionConfig, index) => ({
            id: optionConfig.id || `${question.id}-option-${index}-restored`, // ✅ FIXED: Stable ID
            content:
              optionConfig.content || question.options[index]?.value || "",
            richContent:
              optionConfig.richContent ||
              parseContentWithEquations(
                question.options[index]?.value || "",
                ""
              ),
          }));
        } else {
          // Fallback to API options with stable IDs
          mappedOptions = question.options.map((option, index) => ({
            id: `${question.id}-option-${index}-api`, // ✅ FIXED: Stable ID based on question ID
            content: option.value || "",
            richContent: parseContentWithEquations(option.value || "", ""),
          }));
        }
      }

      // ✅ FIXED: Handle correctAnswers and rightOptions properly
      let correctAnswers =
        questionConfig?.correctAnswers || question.rightOptions || [];
      let rightOptions = questionConfig?.rightOptions || [];

      // ✅ FIXED: If rightOptions is empty but correctAnswers exists, map them to option objects
      if (
        rightOptions.length === 0 &&
        correctAnswers.length > 0 &&
        mappedOptions.length > 0
      ) {
        rightOptions = correctAnswers
          .map((answerIndex) => {
            if (typeof answerIndex === "number" && mappedOptions[answerIndex]) {
              return mappedOptions[answerIndex];
            }
            return answerIndex;
          })
          .filter(Boolean);
      }

      return {
        id: question.id || generateId(),
        serialNumber: question.serialNumber || 1,
        type:
          questionConfig?.type ||
          (hasOptions ? "with-options" : "without-options"),
        content: richContent.plainText,
        richContent: richContent,
        marks: questionConfig?.marks || question.score || 1,
        options: mappedOptions,
        correctAnswers: correctAnswers,
        rightOptions: rightOptions,
        shuffleOptions: questionConfig?.shuffleOptions || false,
        optionsLayout: questionConfig?.optionsLayout || "vertical",
        isMultiSelect:
          questionConfig?.isMultiSelect || question.isMultiSelect || "N",
      };
    });
  };

  // Calculate total marks from all questions
  const calculateTotalMarks = (sections) => {
    return sections.reduce((total, section) => {
      return (
        total +
        section.questions.reduce((sectionTotal, question) => {
          return sectionTotal + (question.marks || 0);
        }, 0)
      );
    }, 0);
  };

  // Map the sections using metadata configuration
  const mappedSections = mapSections(apiData["_TEMPLATE SECTIONS_"]);
  const totalMarks = calculateTotalMarks(mappedSections);

  // Use metadata for exam configuration if available
  const examMetadata = metadataConfig?.examMetadata || {};
  const pagination = metadataConfig?.pagination || { paperSize: "A4" };

  // ✅ UPDATED: Initialize metadata structure from existing data
  const initialMetadata = {};
  mappedSections.forEach((section) => {
    initialMetadata[section.id] = {
      instructions: section.instructions,
      questions: section.questions.map((question) => ({
        questionId: question.id,
        configuration: question.richContent,
      })),
    };
  });

  // Create the exam object in frontend format
  console.log(
    "EXAM TYPE: ",
    apiData["EXAM TYPE"] != null ? apiData["EXAM TYPE"].toUpperCase() : ""
  );
  const examData = {
    id: apiData._ID,
    organizationId:
      getLocalStorageData("profileDetails")["_ORGANIZATION_"]["_ID"],
    regionId: apiData._REGION,
    classId: apiData._CLASS,
    name: apiData.NAME || "New Examination",
    siteId: apiData._SITE,
    academicYearId: apiData["_ACADEMIC YEAR"],
    description: apiData.DESCRIPTION || "Enter exam description here...",
    examType:
      apiData["EXAM TYPE"] != null ? apiData["EXAM TYPE"].toUpperCase() : "",
    sectionId: apiData._SECTION,
    subjectId: apiData._SUBJECT,
    score: apiData.SCORE || 0,
    duration: apiData.DURATION || "00:00:00",
    metadata: {
      heading: examMetadata.heading || apiData.NAME || "EXAMINATION",
      subheading:
        examMetadata.subheading ||
        `${apiData.CLASS || "Standard"} - ${apiData.SECTION || "Section"}`,
      subjectName: examMetadata.subjectName || apiData.SUBJECT || "Subject",
      instructions:
        examMetadata.instructions ||
        "Check the question paper for fairness of printing. If there is any lack of fairness, inform the Hall Supervisor immediately. Answer all questions clearly and concisely.",
      // ✅ NEW: Add rich content structure for instructions
      instructionsRichContent: examMetadata.instructionsRichContent || {
        type: "rich-text",
        html: `<p>${
          examMetadata.instructions ||
          "Check the question paper for fairness of printing. If there is any lack of fairness, inform the Hall Supervisor immediately. Answer all questions clearly and concisely."
        }</p>`,
        plainText:
          examMetadata.instructions ||
          "Check the question paper for fairness of printing. If there is any lack of fairness, inform the Hall Supervisor immediately. Answer all questions clearly and concisely.",
      },
      totalMarks: examMetadata.totalMarks || totalMarks || apiData.SCORE || 0,
      timeAllowed: examMetadata.timeAllowed || parseDuration(apiData.DURATION),
    },
    pagination: pagination,
    pages: metadataConfig?.pages || [
      {
        id: generateId(),
        padding: 20,
        borderStyle: "solid",
        pageBreakBefore: false,
        sections: mappedSections,
      },
    ],
    customEquationTemplates: metadataConfig?.customEquationTemplates || [],
    // ✅ UPDATED: Add metadata to exam structure including equations
    richTextMetadata: initialMetadata,
    // ✅ NEW: Store global equations metadata
    globalEquations: globalEquations,
  };

  console.log("Initial exam data set:", examData);
  return examData;
};

// In utility.js - Update the generateMetadataFromExam function
export const generateMetadataFromExam = (examData) => {
  // ✅ FIXED: Preserve existing global equations
  const existingGlobalEquations = examData.globalEquations || {};
  const extractedEquations = extractAllEquations(examData);
  
  // Merge existing and extracted equations
  const allEquations = {
    ...existingGlobalEquations,
    ...extractedEquations,
  };

  const metadata = {
    examMetadata: {
      heading: examData.metadata.heading,
      subheading: examData.metadata.subheading,
      subjectName: examData.metadata.subjectName,
      instructions: examData.metadata.instructions,
      instructionsRichContent: examData.metadata.instructionsRichContent,
      totalMarks: examData.metadata.totalMarks,
      timeAllowed: examData.metadata.timeAllowed,
    },
    pagination: examData.pagination,
    pages: examData.pages.map((page) => ({
      id: page.id,
      padding: page.padding,
      borderStyle: page.borderStyle,
      pageBreakBefore: page.pageBreakBefore,
      sections: page.sections.map((section) => ({
        id: section.id,
        title: section.title,
        titleRichContent: section.titleRichContent, 
        titleAlignment: section.titleAlignment,
        hasInstructions: section.hasInstructions,
        instructions: section.instructions,
        questions: section.questions.map((question) => ({
          id: question.id,
          serialNumber: question.serialNumber,
          type: question.type,
          content: question.content,
          richContent: question.richContent,
          marks: question.marks,
          options: question.options.map((option) => ({
            id: option.id,
            content: option.content,
            richContent: option.richContent,
          })),
          correctAnswers: question.correctAnswers,
          shuffleOptions: question.shuffleOptions,
          optionsLayout: question.optionsLayout,
          isMultiSelect: question.isMultiSelect,
        })),
      })),
    })),
    customEquationTemplates: examData.customEquationTemplates || [],
    richTextMetadata: examData.richTextMetadata || {},
    // ✅ FIXED: Preserve all equations including existing ones
    equationsMetadata: allEquations,
  };

  return JSON.stringify(metadata);
};

// ✅ NEW: Helper function to extract all equations from exam data
const extractAllEquations = (examData) => {
  const allEquations = {};

  examData.pages.forEach((page) => {
    page.sections.forEach((section) => {
      // Extract equations from section instructions
      if (section.instructions && section.instructions.equations) {
        Object.assign(allEquations, section.instructions.equations);
      }

      // Extract equations from questions
      section.questions.forEach((question) => {
        if (question.richContent && question.richContent.equations) {
          Object.assign(allEquations, question.richContent.equations);
        }

        // Extract equations from options
        question.options.forEach((option) => {
          if (option.richContent && option.richContent.equations) {
            Object.assign(allEquations, option.richContent.equations);
          }
        });
      });
    });
  });

  return allEquations;
};

// Built-in equation templates with enhanced categorization
export const EQUATION_TEMPLATES = [
  {
    id: "fraction",
    name: "Fraction",
    category: "Basic",
    latex: "\\frac{a}{b}",
    preview: "a/b",
  },
  {
    id: "quadratic",
    name: "Quadratic Formula",
    category: "Algebra",
    latex: "x=\\frac{-b\\pm\\sqrt{b^2-4ac}}{2a}",
    preview: "x = (-b ± √(b² - 4ac))/2a",
  },
  {
    id: "integral",
    name: "Integral",
    category: "Calculus",
    latex: "\\int_{a}^{b}f\\left(x\\right)dx",
    preview: "∫[a to b] f(x) dx",
  },
  {
    id: "matrix",
    name: "2x2 Matrix",
    category: "Linear Algebra",
    latex: "\\begin{pmatrix}a&b\\\\c&d\\end{pmatrix}",
    preview: "[a b; c d]",
  },
  {
    id: "summation",
    name: "Summation",
    category: "Calculus",
    latex: "\\sum_{i=1}^{n}x_i",
    preview: "Σ(i=1 to n) xᵢ",
  },
  {
    id: "limit",
    name: "Limit",
    category: "Calculus",
    latex: "\\lim_{x\\to a}f\\left(x\\right)",
    preview: "lim(x→a) f(x)",
  },
  {
    id: "derivative",
    name: "Derivative",
    category: "Calculus",
    latex: "\\frac{d}{dx}f\\left(x\\right)",
    preview: "d/dx f(x)",
  },
  {
    id: "sqrt",
    name: "Square Root",
    category: "Basic",
    latex: "\\sqrt{x}",
    preview: "√x",
  },
  {
    id: "power",
    name: "Exponent",
    category: "Basic",
    latex: "x^{n}",
    preview: "xⁿ",
  },
  {
    id: "log",
    name: "Logarithm",
    category: "Functions",
    latex: "\\log_a\\left(x\\right)",
    preview: "log_a(x)",
  },
];

// Enhanced Initial Exam Data
export const initialExam = {
  id: generateId(),
  metadata: {
    heading: "COMMON QUARTERLY EXAMINATION - SEPTEMBER 2024",
    subheading: "Standard - 12",
    subjectName: "PART - III - MATHEMATICS",
    instructions:
      "Check the question paper for fairness of printing. If there is any lack of fairness, inform the Hall Supervisor immediately. Answer all questions clearly and concisely.",
    totalMarks: 90,
    timeAllowed: "2.30 Hours",
  },
  pagination: {
    paperSize: "A4",
  },
  pages: [
    {
      id: generateId(),
      padding: 20,
      borderStyle: "solid",
      pageBreakBefore: false,
      sections: [
        {
          id: generateId(),
          title: "PART - I",
          titleAlignment: "left",
          marksAllotted: 20,
          questions: [
            {
              id: generateId(),
              type: "with-options",
              content: "The rank of the matrix is",
              richContent: {
                type: "rich-text",
                html: "<p>The <strong>rank</strong> of the matrix is</p>",
                plainText: "The rank of the matrix is",
              },
              marks: 1,
              options: [
                { id: generateId(), content: "2" },
                { id: generateId(), content: "4" },
                { id: generateId(), content: "6" },
                { id: generateId(), content: "8" },
              ],
              correctAnswers: [0],
            },
          ],
        },
      ],
    },
  ],
  customEquationTemplates: [],
  // ✅ NEW: Add initial metadata structure
  richTextMetadata: {},
};

export const setInitalExamData = (data) => {};
// Context
export const ExamContext = createContext(null);

// Helper to check if MathLive is loaded
export const isMathLiveLoaded = () => {
  return window.MathLive && window.customElements.get("math-field");
};

// Page Measurement Utility
export const measureContent = (element) => {
  if (!element) return { height: 0, width: 0 };

  const rect = element.getBoundingClientRect();
  const computedStyle = window.getComputedStyle(element);

  return {
    height:
      rect.height +
      parseFloat(computedStyle.marginTop) +
      parseFloat(computedStyle.marginBottom),
    width:
      rect.width +
      parseFloat(computedStyle.marginLeft) +
      parseFloat(computedStyle.marginRight),
  };
};
export const convertMathEquationsToImages = async (examData) => {
  const mathImages = {};
  const allEquations = extractAllEquationsFromExam(examData);

  if (Object.keys(allEquations).length === 0) {
    return mathImages;
  }

  // Create a temporary container for rendering math
  const tempContainer = document.createElement("div");
  tempContainer.style.cssText = `
    position: absolute;
    top: -9999px;
    left: -9999px;
    background: white;
    padding: 20px;
    font-family: 'Times New Roman', serif;
    font-size: 14px;
  `;
  document.body.appendChild(tempContainer);

  try {
    // Process each equation
    for (const [equationId, equation] of Object.entries(allEquations)) {
      try {
        // Create math field element
        const mathElement = document.createElement("math-field");
        mathElement.style.cssText = `
          display: inline-block;
          font-size: 14px;
          font-family: 'Times New Roman', serif;
          background: white;
          border: none;
          padding: 4px 8px;
        `;
        mathElement.setAttribute("read-only", "true");
        mathElement.value = equation.latex;

        tempContainer.appendChild(mathElement);

        // Wait for math field to render
        await new Promise((resolve) => setTimeout(resolve, 200));

        // Convert to image
        const canvas = await html2canvas(mathElement, {
          backgroundColor: "white",
          scale: 2,
          useCORS: true,
          allowTaint: false,
        });
        

        mathImages[equationId] = canvas.toDataURL("image/png", 1.0);

        // Clean up
        tempContainer.removeChild(mathElement);
      } catch (error) {
        console.warn(`Failed to convert equation ${equationId}:`, error);
        // Fallback: just use the latex text
        mathImages[equationId] = null;
      }
    }
  } finally {
    // Clean up container
    document.body.removeChild(tempContainer);
  }

  return mathImages;
};

const extractAllEquationsFromExam = (examData) => {
  const allEquations = {};

  // Extract from global equations
  if (examData.globalEquations) {
    Object.assign(allEquations, examData.globalEquations);
  }

  // Extract from pages
  examData.pages.forEach((page) => {
    page.sections.forEach((section) => {
      // From section instructions
      if (section.instructions?.equations) {
        Object.assign(allEquations, section.instructions.equations);
      }

      // From questions
      section.questions.forEach((question) => {
        if (question.richContent?.equations) {
          Object.assign(allEquations, question.richContent.equations);
        }

        // From options
        question.options?.forEach((option) => {
          if (option.richContent?.equations) {
            Object.assign(allEquations, option.richContent.equations);
          }
        });
      });
    });
  });

  return allEquations;
};

export const EnhancedRichTextEditor = ({
  value = "",
  onChange = () => {},
  placeholder = "Enter content...",
  className = "",
  disabled = false,
  showToolbar = true,
  maxHeight = "400px",
  allowPasteFormatting = true,
  showMathTools = true,
  isEditMode = false,
  showDeleteButton = false,
  onDelete = null,
  // ✅ NEW: Add metadata context props
  onMetadataChange = null,
  metadataContext = null,
}) => {
  const editorRef = useRef(null);
  const [content, setContent] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMathEditor, setShowMathEditor] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [mathLatex, setMathLatex] = useState("");
  const [editingMathId, setEditingMathId] = useState(null);

  // ✅ ADD: Store cursor position for precise insertion
  const [savedRange, setSavedRange] = useState(null);
  const [savedSelection, setSavedSelection] = useState(null);

  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    justifyLeft: false,
    justifyCenter: false,
    justifyRight: false,
    insertUnorderedList: false,
    insertOrderedList: false,
  });
  const [currentFontSize, setCurrentFontSize] = useState("3");
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");

  // ✅ ADD: Function to save current cursor position
  const saveSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && editorRef.current) {
      const range = selection.getRangeAt(0);

      // Only save if the range is within our editor
      if (
        editorRef.current.contains(range.commonAncestorContainer) ||
        editorRef.current === range.commonAncestorContainer
      ) {
        setSavedRange(range.cloneRange());
        setSavedSelection({
          startContainer: range.startContainer,
          startOffset: range.startOffset,
          endContainer: range.endContainer,
          endOffset: range.endOffset,
        });
        console.log("Selection saved at:", {
          startOffset: range.startOffset,
          endOffset: range.endOffset,
          text: range.toString(),
        });
      }
    }
  }, []);

  // ✅ ADD: Function to restore cursor position
  const restoreSelection = useCallback(() => {
    if (!savedRange || !editorRef.current) {
      console.log("No saved range to restore");
      return false;
    }

    try {
      const selection = window.getSelection();
      selection.removeAllRanges();

      // Ensure the saved range is still valid
      if (
        editorRef.current.contains(savedRange.startContainer) &&
        editorRef.current.contains(savedRange.endContainer)
      ) {
        selection.addRange(savedRange);
        editorRef.current.focus();
        console.log("Selection restored successfully");
        return true;
      } else {
        // Fallback: place cursor at end
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        selection.addRange(range);
        setSavedRange(range);
        editorRef.current.focus();
        console.log("Selection restored to end (fallback)");
        return true;
      }
    } catch (e) {
      console.warn("Could not restore selection:", e);
      return false;
    }
  }, [savedRange]);

  const updateActiveFormats = useCallback(() => {
    if (!editorRef.current || disabled) return;

    try {
      const formats = {
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strikethrough: document.queryCommandState("strikeThrough"),
        justifyLeft: document.queryCommandState("justifyLeft"),
        justifyCenter: document.queryCommandState("justifyCenter"),
        justifyRight: document.queryCommandState("justifyRight"),
        insertUnorderedList: document.queryCommandState("insertUnorderedList"),
        insertOrderedList: document.queryCommandState("insertOrderedList"),
      };

      setActiveFormats(formats);

      // Get current font size
      const fontSize = document.queryCommandValue("fontSize") || "3";
      setCurrentFontSize(fontSize);
    } catch (error) {
      console.warn("Error checking command states:", error);
    }
  }, [disabled]);

  // ✅ ADD: Handle double-click to edit math equations
  const handleEditorDoubleClick = useCallback((e) => {
    const mathElement = e.target.closest(".math-inline");
    if (mathElement && !e.target.classList.contains("math-delete-btn")) {
      e.preventDefault();
      e.stopPropagation();

      const latex = mathElement.getAttribute("data-latex");
      const equationId = mathElement.getAttribute("data-equation-id");

      if (latex) {
        console.log("Double-click detected on equation element:", {
          latex,
          equationId,
        });

        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNode(mathElement);
        setSavedRange(range);

        setMathLatex(latex);
        setEditingMathId(equationId);
        setShowMathEditor(true);
        setShowTemplates(false);

        editorRef.current?.focus();
      }
    }
  }, []);

  // In utility.js - Update the updateContent function in EnhancedRichTextEditor
  const updateContent = useCallback(() => {
    if (!editorRef.current || !onChange) return;

    const htmlContent = editorRef.current.innerHTML;
    setContent(htmlContent);

    // ✅ FIXED: Extract equations from the HTML content
    const equations = {};
    let processedHtml = htmlContent;

    // Find all equation elements and extract their data
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    const equationElements = tempDiv.querySelectorAll("[data-equation-id]");
    equationElements.forEach((element) => {
      const equationId = element.getAttribute("data-equation-id");
      const latex = element.getAttribute("data-latex");

      if (equationId && latex) {
        equations[equationId] = {
          id: equationId,
          latex: latex,
        };

        // ✅ FIXED: Replace the math element with a simple placeholder
        const placeholder = document.createTextNode(`[EQUATION:${equationId}]`);
        element.parentNode.replaceChild(placeholder, element);
      }
    });

    // Get the processed HTML with placeholders
    processedHtml = tempDiv.innerHTML;

    // Generate plain text (equations will show as placeholders)
    const plainTextWithPlaceholders = processedHtml
      .replace(/<[^>]*>/g, "")
      .trim();

    // ✅ FIXED: Create structured content with equations metadata
    const structuredContent = {
      type: "rich-text",
      html: processedHtml,
      plainText: plainTextWithPlaceholders || placeholder,
      equations: Object.keys(equations).length > 0 ? equations : undefined,
    };

    onChange(structuredContent);
  }, [onChange, placeholder]);

  // ✅ IMPROVED: Enhanced click handler with better math element handling
  const handleEditorClick = useCallback(
    (e) => {
      // Handle delete button clicks
      if (e.target.classList.contains("math-delete-btn")) {
        e.preventDefault();
        e.stopPropagation();

        const mathElement = e.target.closest(".math-inline");
        if (mathElement) {
          const mathId = mathElement.getAttribute("data-math-id");
          console.log("Delete button clicked for math element:", mathId);

          // Remove the math element
          mathElement.remove();
          updateContent();

          // Place cursor where the math element was
          const selection = window.getSelection();
          const range = document.createRange();

          // Find the next text node or create one
          const nextNode = mathElement.nextSibling;
          if (nextNode && nextNode.nodeType === Node.TEXT_NODE) {
            range.setStart(nextNode, 0);
          } else {
            // Create a text node and place cursor there
            const textNode = document.createTextNode("\u00A0");
            if (mathElement.parentNode) {
              mathElement.parentNode.insertBefore(textNode, nextNode);
              range.setStart(textNode, 1);
            }
          }

          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
          setSavedRange(range);
        }
        return;
      }

      // Handle regular math element clicks (single click for selection, not editing)
      if (e.target.closest(".math-inline")) {
        const mathElement = e.target.closest(".math-inline");
        console.log("Single click on math element - selecting");

        // Just select the math element for potential operations
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNode(mathElement);
        selection.removeAllRanges();
        selection.addRange(range);
        setSavedRange(range);
        return;
      }

      // Save selection for regular clicks
      setTimeout(() => {
        saveSelection();
        updateActiveFormats();
      }, 10);
    },
    [saveSelection, updateActiveFormats, updateContent]
  );

  const deleteMathElement = useCallback(
    (equationId) => {
      if (!equationId || !editorRef.current) {
        console.log("Invalid equation deletion request:", {
          equationId,
          hasEditor: !!editorRef.current,
        });
        return;
      }

      const mathElement = editorRef.current.querySelector(
        `[data-equation-id="${equationId}"]`
      );
      if (mathElement) {
        console.log("Deleting equation element:", equationId);

        const selection = window.getSelection();
        const range = document.createRange();

        const nextSibling = mathElement.nextSibling;
        const parentNode = mathElement.parentNode;

        mathElement.remove();

        if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE) {
          range.setStart(nextSibling, 0);
        } else if (parentNode) {
          const textNode = document.createTextNode("\u00A0");
          parentNode.insertBefore(textNode, nextSibling);
          range.setStart(textNode, 1);
        }

        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);

        updateContent();
        editorRef.current.focus();
      } else {
        console.warn("Equation element not found for deletion:", equationId);
      }
    },
    [updateContent]
  );
  const insertMathAtCursor = useCallback(() => {
    if (!mathLatex.trim() || !editorRef.current) {
      console.log("No math content or editor ref");
      return;
    }

    const mathId = editingMathId || `eq-${generateId()}`;
    const deleteButton = isEditMode
      ? `<button class="math-delete-btn" onclick="window.deleteMathElement && window.deleteMathElement('${editingMathId}')" type="button" title="Delete equation">×</button>`
      : "";

    // If we're editing an existing equation, replace it
    if (editingMathId) {
      const existingElement = editorRef.current.querySelector(
        `[data-equation-id="${editingMathId}"]`
      );
      if (existingElement) {
        console.log("Updating existing math element");

        existingElement.setAttribute("data-latex", mathLatex);
        existingElement.innerHTML = `
          ${deleteButton}
          <math-field 
            read-only="true" 
            style="display: inline-block; font-size: inherit; border: none; padding: 0; background: transparent; vertical-align: middle;">
            ${mathLatex}
          </math-field>
        `;

        setTimeout(() => {
          const mathField = existingElement.querySelector("math-field");
          if (mathField && mathField.setOptions) {
            mathField.setOptions({
              readOnly: true,
              virtualKeyboardMode: "off",
            });
            mathField.value = mathLatex;
            mathField.style.display = "none";
            requestAnimationFrame(() => {
              mathField.style.display = "inline-block";
            });
          }
          updateContent();
        }, 100);

        setTimeout(() => {
          const selection = window.getSelection();
          const range = document.createRange();
          range.setStartAfter(existingElement);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
          editorRef.current.focus();
        }, 150);

        setShowMathEditor(false);
        setMathLatex("");
        setEditingMathId(null);
        setSavedRange(null);
        return;
      }
    }

    if (!restoreSelection()) {
      console.log("Could not restore selection, using current selection");
    }

    const selection = window.getSelection();
    let range;

    if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else {
      range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    if (
      !editorRef.current.contains(range.commonAncestorContainer) &&
      editorRef.current !== range.commonAncestorContainer
    ) {
      console.log("Range outside editor, creating new range at end");
      range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    try {
      const deleteButton = isEditMode
        ? `<button class="math-delete-btn" onclick="window.deleteMathElement && window.deleteMathElement('${mathId}')" type="button" title="Delete equation">×</button>`
        : "";

      const mathElement = document.createElement("span");
      mathElement.className = `math-inline ${isEditMode ? "edit-mode" : ""}`;
      // ✅ UPDATED: Use data-equation-id instead of data-math-id
      mathElement.setAttribute("data-equation-id", mathId);
      mathElement.setAttribute("data-latex", mathLatex);
      mathElement.setAttribute("contenteditable", "false");
      mathElement.style.display = "inline-block";
      mathElement.style.verticalAlign = "middle";
      mathElement.style.margin = "0 2px";

      mathElement.innerHTML = `
        ${deleteButton}
        <math-field 
          read-only="true" 
          style="display: inline-block; font-size: inherit; border: none; padding: 0; background: transparent; vertical-align: middle;">
          ${mathLatex}
        </math-field>
      `;

      range.deleteContents();
      range.insertNode(mathElement);

      const spaceNode = document.createTextNode("\u00A0");
      range.setStartAfter(mathElement);
      range.insertNode(spaceNode);

      range.setStartAfter(spaceNode);
      range.collapse(true);

      selection.removeAllRanges();
      selection.addRange(range);

      console.log("Math element inserted successfully");

      setTimeout(() => {
        const newMathField = mathElement.querySelector("math-field");
        if (newMathField && newMathField.setOptions) {
          newMathField.setOptions({
            readOnly: true,
            virtualKeyboardMode: "off",
          });
          newMathField.value = mathLatex;

          newMathField.style.display = "none";
          requestAnimationFrame(() => {
            newMathField.style.display = "inline-block";
          });
        }

        updateContent();
      }, 100);
    } catch (error) {
      console.error("Error inserting math:", error);
      // ✅ UPDATED: Use data-equation-id in fallback HTML too
      const mathHtml = `<span class="math-inline ${
        isEditMode ? "edit-mode" : ""
      }" data-equation-id="${mathId}" data-latex="${mathLatex}" contenteditable="false" style="display: inline-block; vertical-align: middle; margin: 0 2px;">
        ${deleteButton}
        <math-field read-only="true" style="display: inline-block; font-size: inherit; border: none; padding: 0; background: transparent; vertical-align: middle;">${mathLatex}</math-field>
      </span>&nbsp;`;

      document.execCommand("insertHTML", false, mathHtml);
    }

    setTimeout(() => {
      if (window.mathVirtualKeyboard) {
        try {
          const activeMathField = document.querySelector("math-field:focus");
          if (!activeMathField) {
            window.mathVirtualKeyboard.hide();
          }
        } catch (e) {
          console.log("Virtual keyboard cleanup:", e);
        }
      }
    }, 500);

    setShowMathEditor(false);
    setMathLatex("");
    setEditingMathId(null);
    setSavedRange(null);
  }, [mathLatex, editingMathId, isEditMode, restoreSelection, updateContent]);

  // In utility.js - Update the restoreEquationsForEditor function
  const restoreEquationsForEditor = useCallback(
    (content) => {
      if (!content || !content.html) return content;

      // If content has equations, restore them for the editor
      if (content.equations && Object.keys(content.equations).length > 0) {
        let restoredHtml = content.html;

        // Replace placeholders with actual math elements for editing
        Object.values(content.equations).forEach((equation) => {
          // ✅ FIXED: Handle both plain and wrapped placeholders
          const plainPlaceholderPattern = new RegExp(
            `\\[EQUATION:${equation.id}\\]`,
            "g"
          );

          const wrappedPlaceholderPattern = new RegExp(
            `<span[^>]*data-equation-id="${equation.id}"[^>]*>\\[EQUATION:${equation.id}\\]</span>`,
            "g"
          );

          const mathElementHtml = `<span class="math-inline ${
            isEditMode ? "edit-mode" : ""
          }" data-equation-id="${equation.id}" data-latex="${
            equation.latex
          }" contenteditable="false" style="display: inline-block; vertical-align: middle; margin: 0 2px;">
        ${
          isEditMode
            ? `<button class="math-delete-btn" onclick="window.deleteMathElement && window.deleteMathElement('${equation.id}')" type="button" title="Delete equation">×</button>`
            : ""
        }
        <math-field read-only="true" style="display: inline-block; font-size: inherit; border: none; padding: 0; background: transparent; vertical-align: middle;">${
          equation.latex
        }</math-field>
      </span>`;

          // Replace both patterns
          restoredHtml = restoredHtml.replace(
            plainPlaceholderPattern,
            mathElementHtml
          );
          restoredHtml = restoredHtml.replace(
            wrappedPlaceholderPattern,
            mathElementHtml
          );
        });

        return {
          ...content,
          html: restoredHtml,
        };
      }

      return content;
    },
    [isEditMode]
  );
  // Update the useEffect that handles initial content setting:
  useEffect(() => {
    if (editorRef.current && value && !content) {
      console.log("Setting initial content:", value);

      // ✅ UPDATED: Restore equations before setting content
      const contentToSet = restoreEquationsForEditor(value);
      const htmlToSet = contentToSet.html || contentToSet || placeholder;

      editorRef.current.innerHTML = htmlToSet;
      setContent(htmlToSet);

      // ✅ FIXED: Store ref in variable and add null checks
      const currentEditor = editorRef.current;

      // Initialize math fields after setting content
      setTimeout(() => {
        // ✅ ADDED: Check if editor still exists
        if (!currentEditor || !currentEditor.parentNode) {
          console.log("Editor unmounted, skipping math field initialization");
          return;
        }

        const mathFields = currentEditor.querySelectorAll(
          ".math-inline math-field"
        );
        mathFields.forEach((field, index) => {
          setTimeout(() => {
            // ✅ ADDED: Check if editor and field still exist
            if (!currentEditor.parentNode || !field.parentNode) {
              return;
            }

            if (field && field.setOptions) {
              field.setOptions({
                readOnly: true,
                virtualKeyboardMode: "off",
              });

              const parentElement = field.closest(".math-inline");
              if (parentElement) {
                const latex = parentElement.getAttribute("data-latex");
                if (latex) {
                  field.value = latex;

                  // Force re-render
                  setTimeout(() => {
                    // ✅ ADDED: Final check before DOM manipulation
                    if (!field.parentNode) return;

                    const originalDisplay = field.style.display;
                    field.style.display = "none";
                    requestAnimationFrame(() => {
                      if (field.parentNode) {
                        field.style.display = originalDisplay || "inline-block";
                      }
                    });
                  }, 50);
                }
              }
            }
          }, 100 * (index + 1));
        });
      }, 200);
    }
  }, [value, content, placeholder, restoreEquationsForEditor]);
  // Add this useEffect for cleanup in EnhancedRichTextEditor
  useEffect(() => {
    return () => {
      // ✅ NEW: Cleanup function to prevent memory leaks
      if (window.mathVirtualKeyboard) {
        try {
          window.mathVirtualKeyboard.hide();
        } catch (e) {
          console.log("Virtual keyboard cleanup:", e);
        }
      }
    };
  }, []);

  // Format text - simplified and more responsive
  const formatText = useCallback(
    (command, value = null) => {
      if (!editorRef.current || disabled) return;

      editorRef.current.focus();

      try {
        const success = document.execCommand(command, false, value);
        if (success) {
          updateContent();
          // Update formats after a brief delay to ensure DOM is updated
          setTimeout(updateActiveFormats, 10);
        }
      } catch (error) {
        console.warn("Error executing command:", error);
      }
    },
    [disabled, updateActiveFormats, updateContent]
  );

  // Handle input - more responsive
  const handleInput = useCallback(
    (e) => {
      // ✅ FIXED: Use requestAnimationFrame to avoid interrupting typing
      requestAnimationFrame(() => {
        updateContent();
      });
    },
    [updateContent]
  );

  // Handle focus
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    updateActiveFormats();
  }, [updateActiveFormats]);

  // Handle blur
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    updateContent();

    // ✅ FIXED: Update metadata only on blur to prevent focus loss
    if (onMetadataChange && metadataContext) {
      const newContent = editorRef.current?.innerHTML || "";
      const plainText = newContent.replace(/<[^>]*>/g, "").trim();
      const updatedContent = {
        type: "rich-text",
        html: newContent,
        plainText: plainText || placeholder,
      };
      onMetadataChange(metadataContext, updatedContent);
    }
  }, [updateContent, onMetadataChange, metadataContext, placeholder]);

  // ✅ IMPROVED: Handle selection changes
  const handleMouseUp = useCallback(() => {
    saveSelection();
    updateActiveFormats();
  }, [saveSelection, updateActiveFormats]);

  const handleKeyUp = useCallback(() => {
    saveSelection();
    updateActiveFormats();
  }, [saveSelection, updateActiveFormats]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) return;

      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case "b":
            e.preventDefault();
            formatText("bold");
            break;
          case "i":
            e.preventDefault();
            formatText("italic");
            break;
          case "u":
            e.preventDefault();
            formatText("underline");
            break;
          case "z":
            e.preventDefault();
            if (e.shiftKey) {
              formatText("redo");
            } else {
              formatText("undo");
            }
            break;
          case "y":
            e.preventDefault();
            formatText("redo");
            break;
        }
      }

      // Save selection after key press
      setTimeout(() => {
        saveSelection();
        updateActiveFormats();
      }, 10);
    },
    [disabled, formatText, saveSelection, updateActiveFormats]
  );

  // Handle paste
  const handlePaste = useCallback(
    (e) => {
      if (disabled) return;

      if (!allowPasteFormatting) {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        document.execCommand("insertText", false, text);
      }

      setTimeout(() => {
        updateContent();
        updateActiveFormats();
        saveSelection();
      }, 10);
    },
    [
      disabled,
      allowPasteFormatting,
      updateContent,
      updateActiveFormats,
      saveSelection,
    ]
  );

  // Link functions
  const handleLinkSubmit = useCallback(() => {
    if (!linkUrl.trim()) return;

    if (linkText.trim()) {
      const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      formatText("insertHTML", linkHtml);
    } else {
      formatText("createLink", linkUrl);
    }

    setShowLinkModal(false);
    setLinkUrl("");
    setLinkText("");
  }, [linkUrl, linkText, formatText]);

  // Selection change listener
  useEffect(() => {
    const handleSelectionChange = () => {
      if (isFocused) {
        updateActiveFormats();
      }
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [isFocused, updateActiveFormats]);

  // Initialize math elements
  useEffect(() => {
    if (editorRef.current) {
      const mathFields = editorRef.current.querySelectorAll(
        ".math-inline math-field"
      );
      mathFields.forEach((mathField, index) => {
        setTimeout(() => {
          if (mathField && mathField.setOptions) {
            mathField.setOptions({
              readOnly: true,
              virtualKeyboardMode: "off",
            });

            const parentElement = mathField.closest(".math-inline");
            if (parentElement) {
              const latex = parentElement.getAttribute("data-latex");
              if (latex) {
                mathField.value = latex;
              }
            }
          }
        }, 100 * (index + 1));
      });

      // Setup global delete function
      window.deleteMathElement = (mathId) => {
        const mathElement = editorRef.current?.querySelector(
          `[data-math-id="${mathId}"]`
        );
        if (mathElement) {
          mathElement.remove();
          updateContent();
        }
      };
    }
  }, [content, isEditMode, updateContent]);

  // Rest of the component remains the same...
  // [Include all the existing toolbar and other components as before]

  // Toolbar button component
  const ToolbarButton = ({
    command,
    children,
    title,
    value,
    isActive,
    onClick,
    style = {},
  }) => (
    <button
      type="button"
      style={{
        padding: "6px 10px",
        border: "1px solid #ced4da",
        borderRadius: "4px",
        background: isActive ? "#007bff" : "white",
        color: isActive ? "white" : "#495057",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "11px",
        userSelect: "none",
        minWidth: "28px",
        height: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s ease",
        fontWeight: isActive ? "600" : "normal",
        boxShadow: isActive ? "0 2px 4px rgba(0,123,255,0.3)" : "none",
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick || (() => formatText(command, value))}
      title={title}
      disabled={disabled}
    >
      {children}
    </button>
  );

  const Divider = () => (
    <div
      style={{
        width: "1px",
        height: "20px",
        backgroundColor: "#dee2e6",
        margin: "0 4px",
        alignSelf: "center",
      }}
    />
  );

  return (
    <div className={`enhanced-rich-text-editor ${className}`}>
      {showToolbar && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            padding: "8px",
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #dee2e6",
            borderTopLeftRadius: "6px",
            borderTopRightRadius: "6px",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15)",
          }}
        >
          {/* Basic Formatting */}
          <div style={{ display: "flex", gap: "2px" }}>
            <ToolbarButton
              command="bold"
              title="Bold (Ctrl+B)"
              isActive={activeFormats.bold}
            >
              <strong>B</strong>
            </ToolbarButton>
            <ToolbarButton
              command="italic"
              title="Italic (Ctrl+I)"
              isActive={activeFormats.italic}
            >
              <em>I</em>
            </ToolbarButton>
            <ToolbarButton
              command="underline"
              title="Underline (Ctrl+U)"
              isActive={activeFormats.underline}
            >
              <u>U</u>
            </ToolbarButton>
            <ToolbarButton
              command="strikeThrough"
              title="Strikethrough"
              isActive={activeFormats.strikethrough}
            >
              <s>S</s>
            </ToolbarButton>
          </div>

          <Divider />

          {/* Font Size */}
          <select
            style={{
              padding: "2px 4px",
              border: "1px solid #ced4da",
              borderRadius: "3px",
              fontSize: "11px",
              backgroundColor: "white",
              color: "#495057",
              height: "28px",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.5 : 1,
            }}
            value={currentFontSize}
            onChange={(e) => {
              setCurrentFontSize(e.target.value);
              formatText("fontSize", e.target.value);
            }}
            onMouseDown={(e) => e.preventDefault()}
            title="Font Size"
            disabled={disabled}
          >
            <option value="1">8pt</option>
            <option value="2">10pt</option>
            <option value="3">12pt</option>
            <option value="4">14pt</option>
            <option value="5">18pt</option>
            <option value="6">24pt</option>
            <option value="7">36pt</option>
          </select>

          <Divider />

          {/* Alignment */}
          <div style={{ display: "flex", gap: "2px" }}>
            <ToolbarButton
              command="justifyLeft"
              title="Align Left"
              isActive={activeFormats.justifyLeft}
            >
              <AlignLeft size={14} />
            </ToolbarButton>
            <ToolbarButton
              command="justifyCenter"
              title="Center"
              isActive={activeFormats.justifyCenter}
            >
              <AlignCenter size={14} />
            </ToolbarButton>
            <ToolbarButton
              command="justifyRight"
              title="Align Right"
              isActive={activeFormats.justifyRight}
            >
              <AlignRight size={14} />
            </ToolbarButton>
          </div>

          <Divider />

          {/* Lists */}
          <div style={{ display: "flex", gap: "2px" }}>
            <ToolbarButton
              command="insertUnorderedList"
              title="Bullet List"
              isActive={activeFormats.insertUnorderedList}
            >
              <List size={14} />
            </ToolbarButton>
            <ToolbarButton
              command="insertOrderedList"
              title="Numbered List"
              isActive={activeFormats.insertOrderedList}
            >
              <ListOrdered size={14} />
            </ToolbarButton>
          </div>

          <Divider />

          {/* Advanced Tools */}
          <div style={{ display: "flex", gap: "2px" }}>
            <ToolbarButton
              title="Insert Link"
              onClick={() => {
                saveSelection(); // ✅ Save selection before opening modal
                const selection = window.getSelection();
                setLinkText(selection.toString());
                setShowLinkModal(true);
              }}
            >
              <Link size={14} />
            </ToolbarButton>
          </div>

          {showMathTools && (
            <>
              <Divider />
              <div style={{ display: "flex", gap: "2px" }}>
                <ToolbarButton
                  title="Insert Math"
                  onClick={() => {
                    saveSelection(); // ✅ CRITICAL: Save cursor position before opening math editor
                    setShowMathEditor(!showMathEditor);
                  }}
                  isActive={showMathEditor}
                >
                  <Calculator size={14} />
                </ToolbarButton>
                <ToolbarButton
                  title="Math Templates"
                  onClick={() => {
                    saveSelection(); // ✅ Save selection before opening templates
                    setShowTemplates(!showTemplates);
                  }}
                  isActive={showTemplates}
                >
                  <Sigma size={14} />
                </ToolbarButton>
              </div>
            </>
          )}

          <Divider />

          {/* Utility Tools */}
          <div style={{ display: "flex", gap: "2px" }}>
            <ToolbarButton command="removeFormat" title="Clear Formatting">
              <span style={{ fontSize: "9px" }}>Clear</span>
            </ToolbarButton>
            <ToolbarButton command="undo" title="Undo (Ctrl+Z)">
              <Undo size={12} />
            </ToolbarButton>
            <ToolbarButton command="redo" title="Redo (Ctrl+Y)">
              <Redo size={12} />
            </ToolbarButton>
          </div>

          <Divider />

          {/* Expand/Collapse */}
          <ToolbarButton
            title={isExpanded ? "Collapse Editor" : "Expand Editor"}
            onClick={() => setIsExpanded(!isExpanded)}
            isActive={isExpanded}
          >
            <span style={{ fontSize: "12px" }}>{isExpanded ? "⬇" : "⬆"}</span>
          </ToolbarButton>

          {showDeleteButton && onDelete && (
            <>
              <Divider />
              <ToolbarButton
                title="Delete this option"
                onClick={onDelete}
                style={{ background: "#dc3545", color: "white" }}
              >
                <Trash2 size={14} />
              </ToolbarButton>
            </>
          )}
        </div>
      )}

      {/* Templates Panel */}
      {showTemplates && (
        <div
          style={{
            padding: "1rem",
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #dee2e6",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem",
            }}
          >
            <h4 style={{ margin: 0, fontSize: "0.9rem" }}>Math Templates</h4>
            <button
              onClick={() => setShowTemplates(false)}
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              <X size={14} />
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
              gap: "0.5rem",
            }}
          >
            {EQUATION_TEMPLATES.map((template) => (
              <button
                key={template.id}
                onClick={() => {
                  setMathLatex(template.latex);
                  setShowTemplates(false);
                }}
                style={{
                  padding: "0.5rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  background: "white",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  textAlign: "center",
                }}
              >
                {template.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Math Editor Panel */}
      {showMathEditor && (
        <div
          style={{
            padding: "1rem",
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h4 style={{ margin: 0, fontSize: "0.9rem" }}>Insert Math</h4>
            <button
              onClick={() => {
                setShowMathEditor(false);
                setMathLatex("");
                setEditingMathId(null);
                setSavedRange(null); // ✅ Clear saved range when closing
              }}
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              <X size={14} />
            </button>
          </div>
          <MathLiveEditor
            value={mathLatex}
            onChange={setMathLatex}
            placeholder="Enter mathematical expression..."
          />
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
            <button
              onClick={insertMathAtCursor}
              disabled={!mathLatex.trim()}
              style={{
                padding: "0.5rem 1rem",
                background: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: mathLatex.trim() ? "pointer" : "not-allowed",
                opacity: mathLatex.trim() ? 1 : 0.5,
              }}
            >
              Insert Math
            </button>
          </div>
        </div>
      )}

      {/* Editor Content */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning={true}
        onInput={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleEditorClick}
        onDoubleClick={handleEditorDoubleClick} // ✅ ADD: Double-click handler
        onMouseUp={handleMouseUp}
        onKeyUp={handleKeyUp}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        style={{
          minHeight: isExpanded ? "200px" : "120px",
          maxHeight: isExpanded ? "400px" : maxHeight,
          padding: "1rem",
          outline: "none",
          overflowY: "auto",
          fontSize: "1rem",
          lineHeight: "1.6",
          color: "#1e293b",
          backgroundColor: "white",
          borderBottomLeftRadius: "6px",
          borderBottomRightRadius: "6px",
          border: "none",
          fontFamily: "system-ui, -apple-system, sans-serif",
          wordWrap: "break-word",
          overflowWrap: "break-word",
        }}
        data-placeholder={placeholder}
      />

      {/* Link Modal - same as before */}
      {showLinkModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "8px",
              padding: "1.5rem",
              width: "400px",
              maxWidth: "90vw",
            }}
          >
            <h3 style={{ margin: "0 0 1rem 0" }}>Insert Link</h3>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem" }}>
                URL:
              </label>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  boxSizing: "border-box",
                }}
                autoFocus
              />
            </div>
            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem" }}>
                Link Text (optional):
              </label>
              <input
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Link text"
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkUrl("");
                  setLinkText("");
                  restoreSelection(); // ✅ Restore selection when canceling
                }}
                style={{
                  padding: "0.5rem 1rem",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleLinkSubmit}
                disabled={!linkUrl.trim()}
                style={{
                  padding: "0.5rem 1rem",
                  background: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: linkUrl.trim() ? "pointer" : "not-allowed",
                  opacity: linkUrl.trim() ? 1 : 0.5,
                }}
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .enhanced-rich-text-editor {
          border: 1px solid #ced4da;
          border-radius: 6px;
          overflow: hidden;
          background: white;
          transition: all 0.2s ease;
          box-shadow: ${isFocused
            ? "0 0 0 0.2rem rgba(0,123,255,.25)"
            : "0 1px 3px rgba(0,0,0,0.1)"};
        }

        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #6c757d;
          cursor: text;
          font-style: italic;
        }

        [contenteditable]:focus {
          outline: none;
        }

        [contenteditable] a {
          color: #007bff;
          text-decoration: underline;
        }

        [contenteditable] ul,
        [contenteditable] ol {
          margin: 8px 0;
          padding-left: 20px;
        }

        [contenteditable] li {
          margin: 4px 0;
        }

        /* ✅ IMPROVED: Math inline styles for better positioning */
        .math-inline {
          display: inline-block !important;
          vertical-align: middle !important;
          margin: 0 2px !important;
          line-height: 1 !important;
        }

        .math-inline.edit-mode {
          position: relative;
          border: 2px solid #007bff !important;
          border-radius: 6px !important;
          padding: 4px 24px 4px 8px !important;
          background: rgba(0, 123, 255, 0.05) !important;
          cursor: pointer !important;
        }

        .math-delete-btn {
          position: absolute;
          top: -8px;
          right: -8px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #dc3545 !important;
          color: white !important;
          border: 2px solid white !important;
          cursor: pointer !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
          font-size: 12px !important;
          line-height: 1 !important;
          z-index: 10;
        }

        .math-inline:not(.edit-mode) {
          background: transparent !important;
          padding: 2px !important;
          border: none !important;
        }

        /* ✅ CRITICAL: Ensure math-field doesn't break line positioning */
        .math-inline math-field {
          display: inline-block !important;
          vertical-align: middle !important;
          font-size: inherit !important;
          border: none !important;
          padding: 0 !important;
          background: transparent !important;
          line-height: inherit !important;
        }
      `}</style>
    </div>
  );
};

// ✅ NEW: Marks validation function
export const validateQuestionMarks = (
  examData,
  questionId,
  newMarks,
  sectionId = null
) => {
  const totalExamMarks = examData.score || examData.metadata?.totalMarks || 0;
  
  if (newMarks > totalExamMarks) {
    return {
      isValid: false,
      error: `Question marks (${newMarks}) cannot exceed total exam marks (${totalExamMarks})`,
    };
  }

  // Calculate current total marks excluding the question being updated
  let currentTotalMarks = 0;
  examData.pages.forEach((page) => {
    page.sections.forEach((section) => {
      section.questions.forEach((question) => {
        if (question.id !== questionId) {
          currentTotalMarks += question.marks || 1;
        }
      });
    });
  });

  const newTotalMarks = currentTotalMarks + newMarks;
  
  if (newTotalMarks > totalExamMarks) {
    const availableMarks = totalExamMarks - currentTotalMarks;
    return {
      isValid: false,
      error: `Cannot allocate ${newMarks} marks. Only ${availableMarks} marks available (Total: ${totalExamMarks}, Used: ${currentTotalMarks})`,
    };
  }

  return { isValid: true };
};
// ✅ FIXED: RichContentRenderer component - handles object rendering properly
export const RichContentRenderer = ({ content, isEditMode = false }) => {
  const contentRef = useRef(null);
  
  // In utility.js - Update the restoreEquationsInContent function
  const restoreEquationsInContent = useCallback(
    (htmlContent, equations) => {
      if (!equations || Object.keys(equations).length === 0) {
        return htmlContent;
      }

      let restoredHtml = htmlContent;

      // Replace placeholders with actual math elements
      Object.values(equations).forEach((equation, index) => {
        // ✅ FIXED: Handle both wrapped and unwrapped equation placeholders
        const wrappedPlaceholderPattern = new RegExp(
          `<span[^>]*data-equation-id="${
            equation.id
          }"[^>]*>\\[EQUATION:${equation.id}\\]</span>`,
          "g"
        );

        const plainPlaceholderPattern = new RegExp(
          `\\[EQUATION:${equation.id}\\]`,
          "g"
        );

        // ✅ FIXED: Use proper key with string concatenation
        const mathElementHtml = `<span key="${
          equation.id + '-' + index
        }" className="math-inline ${
          isEditMode ? "edit-mode" : ""
        }" data-equation-id="${equation.id}" data-latex="${
          equation.latex
        }" contentEditable="false" style="display: inline-block; vertical-align: middle; margin: 0 2px;">
      ${
        isEditMode
          ? `<button className="math-delete-btn" onclick="window.deleteMathElement && window.deleteMathElement('${equation.id}')" type="button" title="Delete equation">×</button>`
          : ""
      }
      <math-field read-only="true" style="display: inline-block; font-size: inherit; border: none; padding: 0; background: transparent; vertical-align: middle;">${
        equation.latex
      }</math-field>
    </span>`;

        // Replace both wrapped and plain placeholders
        restoredHtml = restoredHtml.replace(
          wrappedPlaceholderPattern,
          mathElementHtml
        );
        restoredHtml = restoredHtml.replace(
          plainPlaceholderPattern,
          mathElementHtml
        );
      });

      return restoredHtml;
    },
    [isEditMode]
  );
  
  // ✅ FIXED: Add effect to watch for content changes and update math fields
  useEffect(() => {
    if (contentRef.current) {
      // ✅ FIXED: Store ref in variable
      const currentContent = contentRef.current;

      const mathFields = currentContent.querySelectorAll(
        ".math-inline math-field"
      );
      mathFields.forEach((field, index) => {
        setTimeout(() => {
          // ✅ ADDED: Check if content and field still exist
          if (!currentContent.parentNode || !field.parentNode) {
            return;
          }

          if (field && field.setOptions) {
            field.setOptions({
              readOnly: true,
              virtualKeyboardMode: "off",
            });

            const parentElement = field.closest(".math-inline");
            if (parentElement) {
              const latex = parentElement.getAttribute("data-latex");
              if (latex && field.value !== latex) {
                field.value = latex;

                // Force re-render to ensure proper display
                setTimeout(() => {
                  // ✅ ADDED: Check before DOM manipulation
                  if (!field.parentNode) return;

                  const originalDisplay = field.style.display;
                  field.style.display = "none";
                  requestAnimationFrame(() => {
                    if (field.parentNode) {
                      field.style.display = originalDisplay || "inline-block";
                    }
                  });
                }, 50);
              }
            }
          }
        }, 100 * (index + 1));
      });
    }
  }, [content]);

  useEffect(() => {
    if (contentRef.current) {
      const mathElements = contentRef.current.querySelectorAll(".math-inline");
      mathElements.forEach((mathElement) => {
        if (!isEditMode) {
          const deleteBtn = mathElement.querySelector(".math-delete-btn");
          if (deleteBtn) {
            deleteBtn.remove();
          }
          mathElement.classList.remove("edit-mode");
        } else {
          mathElement.classList.add("edit-mode");
        }
      });
    }
  }, [isEditMode]);

  // ✅ FIXED: Better content handling to prevent object rendering
  if (!content) return null;

  // ✅ FIXED: Handle different content types properly
  if (content && typeof content === 'object') {
    if (content.type === "rich-text" && content.html) {
      // ✅ UPDATED: Handle structured content with equations
      let htmlContent = content.html;

      // If content has equations metadata, restore them
      if (content.equations) {
        htmlContent = restoreEquationsInContent(htmlContent, content.equations);
      }

      if (!isEditMode) {
        // ✅ FIXED: Updated regex to match 'className' instead of 'class'
        htmlContent = htmlContent.replace(
          /<button[^>]*className="math-delete-btn"[^>]*>.*?<\/button>/g,
          ""
        );
        htmlContent = htmlContent.replace(/\s*edit-mode/g, "");
      }

      return (
        <div ref={contentRef} dangerouslySetInnerHTML={{ __html: htmlContent }} />
      );
    }

    // ✅ FIXED: Handle content object with proper fallbacks
    if (content.content && typeof content.content === 'string') {
      return <span>{content.content}</span>;
    }

    if (content.plainText && typeof content.plainText === 'string') {
      return <span>{content.plainText}</span>;
    }

    // ✅ FIXED: If content is an object but doesn't match expected structure
    if (content.id) {
      console.warn('Attempted to render object with id as React child:', content);
      return <span>[Content Error]</span>;
    }
  }

  // ✅ FIXED: Handle string content
  if (typeof content === "string") {
    return <span>{content}</span>;
  }

  // ✅ FIXED: Fallback for any other cases
  console.warn('Unexpected content type in RichContentRenderer:', typeof content, content);
  return <span>[Invalid Content]</span>;
};
// ✅ NEW: Function to update examination template metadata
// ✅ ALTERNATIVE: Function that takes exam data as parameter
export const updateExaminationTemplateWithMetadata = async (
  examData,
  examId,
  fetchExamDetails
) => {
  try {
    // Generate updated metadata
    const updatedMetadata = generateMetadataFromExam(examData);
    console.log("examData: ", examData);

    // ✅ CRITICAL FIX: Create a copy of examData instead of mutating the original
    const apiPayload = { ...examData };
    delete apiPayload.metadata;
    delete apiPayload.pages;
    delete apiPayload.pagination;
    delete apiPayload.customEquationTemplates;
    delete apiPayload.richTextMetadata;
    delete apiPayload.globalEquations;

    // Add the metadata to the API payload
    apiPayload.metadata = updatedMetadata;

    // Call the update API
    const response = await updateExaminationTemplate(examId, apiPayload);

    if (response.status === "success" || response.status === "200") {
      console.log("Examination template updated successfully");

      // Refresh exam details to get updated data
      if (fetchExamDetails) {
        await fetchExamDetails();
      }

      return { success: true };
    } else {
      console.error("Failed to update examination template:", response);
      return { success: false, error: response.message };
    }
  } catch (error) {
    console.error("Error updating examination template metadata:", error);
    return { success: false, error: error.message };
  }
};

// ✅ NEW: Validation function for total exam marks
export const validateTotalExamMarks = (examData, newTotalMarks) => {
  // Calculate current total allocated marks to all questions
  let currentAllocatedMarks = 0;
  examData.pages.forEach((page) => {
    page.sections.forEach((section) => {
      section.questions.forEach((question) => {
        currentAllocatedMarks += question.marks || 1;
      });
    });
  });

  if (newTotalMarks < currentAllocatedMarks) {
    const questionCount = examData.pages.reduce((total, page) =>
      total + page.sections.reduce((sectionTotal, section) =>
        sectionTotal + section.questions.length, 0
      ), 0
    );

    return {
      isValid: false,
      error: `Cannot set total marks to ${newTotalMarks}. Currently ${currentAllocatedMarks} marks are allocated to ${questionCount} question(s). Please reduce individual question marks first.`,
      currentAllocatedMarks,
      questionCount
    };
  }

  return { isValid: true };
};

// Update the InlineQuestionEditor component in utility.js

// Update the InlineQuestionEditor component in utility.js

// ✅ FIXED: Updated InlineQuestionEditor with stable keys and focus handling
export const InlineQuestionEditor = ({
  question,
  onUpdate,
  onSave,
  onCancel,
  onMetadataChange = null,
  metadataContext = null,
  examData = null,
  examId = null,
  fetchExamDetails = null,
}) => {
  console.log("InlineQuestionEditor props:", {
    question,
    onUpdate,
    onSave,
    onCancel,
    onMetadataChange,
    metadataContext,
    examData,
    examId,
    fetchExamDetails,
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [questionType, setQuestionType] = useState(
    examData.examType=="OBJECTIVE" ? "with-options" : question.type || "without-options"
  );
  const [questionContent, setQuestionContent] = useState(question.richContent);
  const [questionMarks, setQuestionMarks] = useState(question.marks || 1);
  
  // ✅ FIXED: Use stable initialization to prevent re-renders
  const [options, setOptions] = useState(() => {
    if (question.options && question.options.length > 0) {
      return question.options.map((option, index) => ({
        ...option,
        id: option.id || `${question.id}-option-${index}-${Date.now()}`, // ✅ FIXED: Ensure stable ID
      }));
    }
    return [];
  });
  
  const [correctAnswers, setCorrectAnswers] = useState(question.correctAnswers || []);
  const [rightOptions, setRightOptions] = useState(
    question.rightOptions || question.correctAnswers || []
  );
  const [isMultiSelect, setIsMultiSelect] = useState(
    question.isMultiSelect === "Y" || false
  );
  const [optionsLayout, setOptionsLayout] = useState(
    question.optionsLayout || "vertical"
  );

  const handleQuestionContentChange = (content) => {
    setQuestionContent(content);
  };

  const handleQuestionTypeChange = (type) => {
    setQuestionType(type);
    // Clear correct answers when switching to without-options
    if (type === "without-options") {
      setCorrectAnswers([]);
      setRightOptions([]);
    }
  };

  // ✅ FIXED: Stable addOption function that doesn't regenerate on every render
  const addOption = useCallback(() => {
    const newIndex = options.length;
    const newOption = {
      id: newIndex+1, // ✅ FIXED: Stable ID
      content: `Option ${String.fromCharCode(65 + newIndex)}`,
      richContent: {
        type: "rich-text",
        html: `<p>Option ${String.fromCharCode(65 + newIndex)}</p>`,
        plainText: `Option ${String.fromCharCode(65 + newIndex)}`,
      },
    };
    setOptions(prev => [...prev, newOption]);
  }, [options.length, question.id]);

  // ✅ FIXED: Stable removeOption function
  const removeOption = useCallback((index) => {
    const optionToRemove = options[index];

    // Remove the option
    setOptions(prev => prev.filter((_, i) => i !== index));

    // Remove from rightOptions by option id, then update indices
    setRightOptions((prev) => {
      const filteredOptions = prev.filter(
        (opt) => opt.id !== optionToRemove.id
      );
      return filteredOptions;
    });

    // Update correctAnswers indices
    setCorrectAnswers((prev) =>
      prev
        .filter((correctIndex) => correctIndex !== index)
        .map((correctIndex) =>
          correctIndex > index ? correctIndex - 1 : correctIndex
        )
    );
  }, [options]);

  // ✅ FIXED: Stable updateOption function with proper key handling
  const updateOption = useCallback((index, content) => {
    setOptions(prev => prev.map((opt, i) =>
      i === index
        ? {
            ...opt,
            content: content.plainText,
            richContent: content,
          }
        : opt
    ));
  }, []);

  // Handle multiSelect toggle
  const handleMultiSelectChange = (checked) => {
    setIsMultiSelect(checked);
    // If turning off multiSelect and more than one answer is selected, keep only the first one
    if (!checked && rightOptions.length > 1) {
      const firstSelected = rightOptions[0];
      setRightOptions([firstSelected]);
      setCorrectAnswers([firstSelected]);
    }
  };

  // ✅ FIXED: Stable toggleCorrectAnswer function
  const toggleCorrectAnswer = useCallback((index) => {
    const optionObject = options[index];

    setRightOptions((prev) => {
      let newRightOptions;

      // Check if this option is already selected (compare by option id)
      const isAlreadySelected = prev.some((opt) => opt.id === optionObject.id);

      if (isAlreadySelected) {
        // Remove from array
        newRightOptions = prev.filter((opt) => opt.id !== optionObject.id);
      } else {
        // Add to array
        if (!isMultiSelect && prev.length >= 1) {
          // If not multiSelect and already has one answer, replace it
          newRightOptions = [optionObject];
        } else {
          // Add to existing array
          newRightOptions = [...prev, optionObject];
        }
      }

      // Update correctAnswers state to match (keep indices for backward compatibility)
      const newCorrectIndices = newRightOptions
        .map((opt) => options.findIndex((o) => o.id === opt.id))
        .filter((idx) => idx !== -1);
      setCorrectAnswers(newCorrectIndices);

      return newRightOptions;
    });
  }, [options, isMultiSelect]);

  const handleSave = async () => {
    // Validation before saving
    if (questionType === "with-options") {
      // Check if there are options
      if (options.length === 0) {
        alert("Please add at least one option before saving.");
        return;
      }

      // Check if at least one correct answer is selected
      if (rightOptions.length === 0) {
        alert("Please select at least one correct answer before saving.");
        return;
      }
      if(options.length<2){
        alert("Please add at least two options before saving.");
        return;
      }
    }

    setIsSaving(true);

    try {
      const updatedQuestion = {
        ...question,
        type: questionType,
        richContent: questionContent,
        marks: questionMarks,
        options: options,
        optionsLayout: optionsLayout,
        correctAnswers: rightOptions,
        isMultiSelect: isMultiSelect ? "Y" : "N",
        content:
          questionContent.plainText ||
          questionContent.html?.replace(/<[^>]*>/g, "") ||
          "New Question",
      };

      // Update via API first, then fetch fresh data
      if (examData && examId && fetchExamDetails) {
        // Step 1: Update the question via API
        const questionApiData = {
          organizationId: examData.organizationId,
          templateId: examData.id,
          siteId: examData.siteId,
          regionId: examData.regionId,
          id: question.id,
          question:
            questionContent.plainText ||
            questionContent.html?.replace(/<[^>]*>/g, "") ||
            "",
          score: questionMarks,
          serialNumber: question.serialNumber || 1,
          options:
            questionType === "with-options"
              ? options.map((opt, index) => ({
                  id: opt.id,
                  value: "saved in metaData",
                }))
              : [],
          rightOptions: rightOptions.map((opt) => ({
            id: opt.id,
            value: "saved in metaData",
          })),
          isMultiSelect: isMultiSelect ? "Y" : "N",
        };

        const questionResponse = await updateQuestion(
          questionApiData,
          question.id
        );

        if (
          questionResponse.status === "success" ||
          questionResponse.status === "200"
        ) {
          console.log("Question updated successfully via API");

          // Step 2: Update the exam template with updated metadata
          const updatedExamData = {
            ...examData,
            pages: examData.pages.map((page) => ({
              ...page,
              sections: page.sections.map((section) =>
                section.id === metadataContext.sectionId
                  ? {
                      ...section,
                      questions: section.questions.map((q) =>
                        q.id === question.id ? updatedQuestion : q
                      ),
                    }
                  : section
              ),
            })),
          };

          // Generate updated metadata
          const updatedMetadata = generateMetadataFromExam(updatedExamData);

          // Prepare template data
          const templateApiData = {
            ...examData,
            metadata: updatedMetadata,
          };

          delete templateApiData.pages;
          delete templateApiData.pagination;
          delete templateApiData.customEquationTemplates;
          delete templateApiData.richTextMetadata;
          delete templateApiData.globalEquations;
          
          const templateResponse = await updateExaminationTemplate(
            examId,
            templateApiData
          );

          if (
            templateResponse.status === "success" ||
            templateResponse.status === "200"
          ) {
            console.log("Exam template metadata updated successfully");
            // Fetch fresh data from API
            await fetchExamDetails();
          } else {
            console.error("Failed to update exam template:", templateResponse);
            throw new Error(
              templateResponse.message || "Failed to update exam template"
            );
          }
        } else {
          console.error("Failed to update question via API:", questionResponse);
          throw new Error(
            questionResponse.message || "Failed to update question"
          );
        }
      } else {
        // Fallback: update local state only if API update is not available
        onUpdate(updatedQuestion);
      }

      onSave();
    } catch (error) {
      console.error("Error saving question:", error);
      alert("Failed to save question. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="inline-question-editor">
      <div className="question-editor-header">
        {examData.examType !== "SUBJECTIVE" && (
          <div className="question-type-section">
            <label>Type:</label>
            <select
              value={questionType}
              onChange={(e) => handleQuestionTypeChange(e.target.value)}
              className="question-type-select"
            >
              {examData.examType == "OBJECTIVE" ? null : (
                <option value="without-options">Without Options</option>
              )}
              <option value="with-options">With Options</option>
            </select>
          </div>
        )}
        {questionType == "with-options" && (
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="flexSwitchCheckChecked"
              checked={isMultiSelect}
              onChange={(e) => handleMultiSelectChange(e.target.checked)}
            />
            <label
              className="form-check-label"
              htmlFor="flexSwitchCheckChecked"
            >
              isMultiSelect
            </label>
          </div>
        )}

        <div className="question-marks-section">
          <label>Marks:</label>
          <input
            type="number"
            value={questionMarks}
            onChange={(e) => {
              const newMarks = parseInt(e.target.value, 10) || 1;

              if (examData && question.id) {
                const validation = validateQuestionMarks(
                  examData,
                  question.id,
                  newMarks
                );
                if (validation.isValid) {
                  setQuestionMarks(newMarks);
                } else {
                  console.warn(validation.error);
                }
              } else {
                setQuestionMarks(newMarks);
              }
            }}
            className="question-marks-input"
            min="1"
            max="100"
          />
        </div>

        {questionType === "with-options" && (
          <div className="options-layout-section">
            <label>Layout:</label>
            <select
              value={optionsLayout}
              onChange={(e) => setOptionsLayout(e.target.value)}
              className="options-layout-select"
            >
              <option value="vertical">Vertical</option>
              <option value="horizontal">Horizontal</option>
              <option value="grid">Grid (2 columns)</option>
            </select>
          </div>
        )}

        <div className="editor-actions">
          <button
            onClick={handleSave}
            className="save-question-btn"
            disabled={isSaving}
          >
            <Check size={14} />
            <span>{isSaving ? "Saving..." : "Save"}</span>
          </button>
          <button onClick={onCancel} className="cancel-question-btn">
            <X size={14} />
            Cancel
          </button>
        </div>
      </div>

      <div className="question-content-editor">
        <label>Question Content:</label>
        <EnhancedRichTextEditor
          value={questionContent}
          onChange={handleQuestionContentChange}
          placeholder="Enter your question here..."
          isEditMode={true}
          onMetadataChange={onMetadataChange}
          metadataContext={metadataContext}
        />
      </div>

      {questionType == "with-options" && (
        <div className="options-editor">
          <div className="options-header">
            <label>Answer Options:</label>
            <button onClick={addOption} className="add-option-btn">
              <Plus size={14} />
              Add Option
            </button>
          </div>

          <div className="correct-answer-instruction">
            <span className="instruction-text">
              Please select at least one correct answer
              {!isMultiSelect &&
                " (Single answer mode - only one option can be selected)"}
              {isMultiSelect && " (Multiple answers allowed)"}
            </span>
            <span className="instruction-checkbox">
              <div className="sample-checkbox checked"></div>
              (Click the green circle to mark correct answers)
            </span>
          </div>

          <div className="options-list">
            {options.map((option, index) => (
              <div key={option.id} className="option-editor">
                {" "}
                {/* ✅ FIXED: Use stable option.id as key */}
                <div className="option-header">
                  <div className="option-label">
                    {String.fromCharCode(65 + index)}
                  </div>
                  <button
                    type="button"
                    className={`correct-answer-checkbox ${
                      rightOptions.some((opt) => opt.id === option.id)
                        ? "checked"
                        : ""
                    }`}
                    onClick={() => {
                      const isCurrentlySelected = rightOptions.some(
                        (opt) => opt.id === option.id
                      );

                      if (
                        !isMultiSelect &&
                        !isCurrentlySelected &&
                        rightOptions.length >= 1
                      ) {
                        if (
                          window.confirm(
                            "Multiple selection is disabled. Selecting this option will deselect the current correct answer. Continue?"
                          )
                        ) {
                          toggleCorrectAnswer(index);
                        }
                      } else {
                        toggleCorrectAnswer(index);
                      }
                    }}
                    title={
                      rightOptions.some((opt) => opt.id === option.id)
                        ? "Remove as correct answer"
                        : "Mark as correct answer"
                    }
                  >
                    {rightOptions.some((opt) => opt.id === option.id) && (
                      <Check size={12} />
                    )}
                  </button>
                </div>
                <div className="option-content">
                  <EnhancedRichTextEditor
                    key={`editor-${option.id}`}
                    value={
                      option.richContent || {
                        type: "rich-text",
                        html: `<p>${option.content}</p>`,
                        plainText: option.content,
                      }
                    }
                    onChange={(content) => updateOption(index, content)}
                    placeholder={`Option ${String.fromCharCode(65 + index)}`}
                    showMathTools={true}
                    isEditMode={true}
                    showDeleteButton={true}
                    onDelete={() => removeOption(index)}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Validation messages */}
          {questionType === "with-options" && (
            <div className="validation-messages">
              {options.length === 0 && (
                <div className="validation-message error">
                  <span className="warning-icon">❌</span>
                  Please add at least one option before saving.
                </div>
              )}

              {options.length > 0 && rightOptions.length === 0 && (
                <div className="validation-message warning">
                  <span className="warning-icon">⚠️</span>
                  Please select at least one correct answer before saving.
                </div>
              )}

              {rightOptions.length > 1 && !isMultiSelect && (
                <div className="validation-message info">
                  <span className="warning-icon">ℹ️</span>
                  Multiple selection is disabled. Only one correct answer will
                  be saved.
                </div>
              )}

              {rightOptions.length > 0 && (
                <div className="validation-message success">
                  <span className="warning-icon">✅</span>
                  {rightOptions.length} correct answer
                  {rightOptions.length > 1 ? "s" : ""} selected.
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <style jsx>{`
        /* ✅ NEW: Styles for correct answer instruction */
        .correct-answer-instruction {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
          padding: 0.75rem;
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 6px;
          font-size: 0.875rem;
          color: #0369a1;
        }

        .instruction-text {
          font-weight: 500;
        }

        .instruction-checkbox {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.8rem;
          color: #0284c7;
        }

        .sample-checkbox {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 2px solid #10b981;
          background: #10b981;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 10px;
        }

        .sample-checkbox::after {
          content: "✓";
          font-size: 10px;
          font-weight: bold;
        }

        /* ✅ NEW: Styles for option header and checkbox */
        .option-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .correct-answer-checkbox {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 2px solid #d1d5db;
          background: #ffffff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          color: white;
          font-weight: bold;
        }

        .correct-answer-checkbox:hover {
          border-color: #10b981;
          background: #f0fdf4;
        }

        .correct-answer-checkbox.checked {
          border-color: #10b981;
          background: #10b981;
          box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
        }

        .correct-answer-checkbox.checked:hover {
          background: #059669;
          border-color: #059669;
        }

        /* ✅ UPDATED: Enhanced validation message styles */
        .validation-messages {
          margin-top: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .validation-message {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .validation-message.error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
        }

        .validation-message.warning {
          background: #fef3c7;
          border: 1px solid #fbbf24;
          color: #92400e;
        }

        .validation-message.info {
          background: #eff6ff;
          border: 1px solid #93c5fd;
          color: #2563eb;
        }

        .validation-message.success {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #16a34a;
        }

        .warning-icon {
          font-size: 1rem;
        }

        /* ✅ UPDATE: Modified option-editor to accommodate new header */
        .option-editor {
          padding: 1rem;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .option-editor:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }

        .option-label {
          color: #64748b;
          font-weight: 600;
          font-size: 0.875rem;
          flex-shrink: 0;
        }

        .option-content {
          width: 100%;
          min-width: 0;
        }

        /* Existing styles remain the same... */
        .question-editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .question-type-section,
        .question-marks-section,
        .options-layout-section {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .question-type-section label,
        .question-marks-section label,
        .options-layout-section label {
          font-weight: 500;
          color: #374151;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .question-type-select,
        .question-marks-input,
        .options-layout-select {
          padding: 0.5rem 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.9rem;
          transition: all 0.2s ease;
          background: #ffffff;
          min-width: 120px;
        }

        .question-marks-input {
          width: 80px;
          min-width: 80px;
        }

        .options-layout-select {
          min-width: 140px;
        }

        .question-type-select:focus,
        .question-marks-input:focus,
        .options-layout-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .editor-actions {
          display: flex;
          gap: 0.75rem;
          flex-shrink: 0;
        }

        .save-question-btn {
          padding: 0.625rem 1.25rem;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(16, 185, 129, 0.3);
          white-space: nowrap;
        }

        .save-question-btn:hover {
          background: #059669;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
        }

        .cancel-question-btn {
          padding: 0.625rem 1.25rem;
          background: #6b7280;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .cancel-question-btn:hover {
          background: #4b5563;
          transform: translateY(-1px);
        }

        .question-content-editor {
          margin-bottom: 2rem;
          width: 100%;
        }

        .question-content-editor label {
          display: block;
          margin-bottom: 0.75rem;
          font-weight: 500;
          color: #374151;
          font-size: 0.9rem;
        }

        .options-editor {
          margin-top: 2rem;
          width: 100%;
        }

        .options-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .options-header label {
          font-weight: 500;
          color: #374151;
          font-size: 0.9rem;
          margin: 0;
        }

        .add-option-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.875rem;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.8rem;
          font-weight: 500;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(59, 130, 246, 0.3);
          white-space: nowrap;
        }

        .add-option-btn:hover {
          background: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
        }

        .options-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        /* Responsive adjustments */
        @media (max-width: 1024px) {
          .question-editor-header {
            flex-direction: column;
            align-items: stretch;
          }

          .question-type-section,
          .question-marks-section,
          .options-layout-section {
            justify-content: center;
          }

          .editor-actions {
            justify-content: center;
          }
        }

        @media (max-width: 768px) {
          .question-type-section,
          .question-marks-section,
          .options-layout-section {
            flex-direction: column;
            align-items: center;
          }

          .save-question-btn,
          .cancel-question-btn {
            padding: 0.5rem 1rem;
            font-size: 0.8rem;
          }

          .option-editor {
            padding: 0.75rem;
          }

          .options-header {
            flex-direction: column;
            gap: 0.5rem;
            align-items: stretch;
          }

          .add-option-btn {
            justify-content: center;
          }

          .correct-answer-instruction {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
          }
        }

        @media (max-width: 480px) {
          .question-type-section,
          .question-marks-section,
          .options-layout-section {
            flex-direction: column;
            align-items: center;
          }

          .save-question-btn,
          .cancel-question-btn {
            padding: 0.5rem 1rem;
            font-size: 0.8rem;
          }

          .options-list {
            gap: 0.75rem;
          }

          .option-editor {
            padding: 0.625rem;
          }
        }
      `}</style>
    </div>
  );
};

/**
 * Enhanced Page Settings Modal Component
 */
export const PageSettingsModal = ({ isOpen, onClose, exam, onUpdate }) => {
  const [paperSize, setPaperSize] = useState(exam.pagination.paperSize);

  const handleSave = () => {
    onUpdate((exam) => ({
      ...exam,
      pagination: {
        ...exam.pagination,
        paperSize,
      },
    }));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Page Settings</h3>
          <button onClick={onClose} className="modal-close-btn">
            <X size={16} />
          </button>
        </div>

        <div className="modal-body">
          <div className="setting-group">
            <label>Paper Size:</label>
            <select
              value={paperSize}
              onChange={(e) => setPaperSize(e.target.value)}
              className="setting-select"
            >
              {Object.entries(PAPER_SIZES).map(([key, size]) => (
                <option key={key} value={key}>
                  {size.name}
                </option>
              ))}
            </select>
          </div>

          <div className="setting-info">
            <p>
              <strong>Current paper size:</strong> {PAPER_SIZES[paperSize].name}
            </p>
            <p>
              <strong>Dimensions:</strong> {PAPER_SIZES[paperSize].printWidth} ×{" "}
              {PAPER_SIZES[paperSize].printHeight}
            </p>
            <p>
              Pages are automatically organized based on content length and will
              match the PDF output exactly.
            </p>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={handleSave} className="modal-save-btn">
            <Check size={14} />
            Save Settings
          </button>
          <button onClick={onClose} className="modal-cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Utility function to calculate required pages based on content
export const calculateRequiredPages = (exam, paperSize = "A4") => {
  const pageSize = PAPER_SIZES[paperSize];
  const availableHeight = pageSize.height - 200;

  const questionHeight = 100;
  const sectionHeaderHeight = 50;
  const examHeaderHeight = 200;

  let totalContentHeight = examHeaderHeight;

  exam.pages.forEach((page) => {
    page.sections.forEach((section) => {
      totalContentHeight += sectionHeaderHeight;
      totalContentHeight += section.questions.length * questionHeight;

      section.questions.forEach((question) => {
        if (question.type === "with-options" && question.options) {
          totalContentHeight += question.options.length * 30;
        }
      });
    });
  });

  return Math.ceil(totalContentHeight / availableHeight);
};

// Helper functions for finding elements by ID
export const findSectionById = (exam, sectionId) => {
  for (let page of exam.pages) {
    for (let section of page.sections) {
      if (section.id === sectionId) {
        return { section, page };
      }
    }
  }
  return null;
};

export const findQuestionById = (exam, questionId) => {
  for (let page of exam.pages) {
    for (let section of page.sections) {
      for (let question of section.questions) {
        if (question.id === questionId) {
          return { question, section, page };
        }
      }
    }
  }
  return null;
};
